<script setup lang="ts">
/**
 * 授權申請＝**審核流程主畫面**（`/requests`，admin+）——批93-C §2.2、批108 用語。
 *
 * 狀態機（後端 `license_requests.status`）：
 *   `draft` ──確認送出──▶ `pending` ──審核通過──▶ `approved`
 *      ▲                    │  └─婉拒──▶ `rejected`
 *      └────退回（附理由）───┘   └─撤回──▶ `cancelled`
 *
 * ★★ 三件與批93-C 之前完全不同、改動時最容易搞錯的事：
 *
 * ① **審核不能改內容**。`ApproveInput` 只剩 `review_note`——審核者能改內容的話，
 *    送件方確認過的東西就不算數，審核整個失去意義。要改內容就「退回草稿」。
 * ② **「已退回」不是狀態**。`return` 把 pending 打回 `draft` 並在 `review.reason` 留理由，
 *    所以畫面上它是「草稿頁籤裡有退回理由的那些」，不是第六個頁籤。
 * ③ **草稿有所有權**。我方草稿（`origin='admin'`）我方 admin+ 可改；代理商草稿我方
 *    **只能退回或附註記**（後端 PUT 回 403 `not_owner`）——憑證內容是送件那一方的責任。
 *
 * 誰能按「核發」由**套裝的核發政策**決定，不是角色寫死：`activation_policy` 是
 * 「可免審直接核發的最低身分」，且內容偏離模板時強制升為 superadmin（`lib/activation.ts`
 * 是前端鏡像，後端才是權威）。★ 批108 起**自己送出的申請自己也能核發**（那道 409 已拿掉），
 *   稽核會留下 `self_approved` 供事後追查。
 */
import { computed, nextTick, onMounted, ref, watch } from 'vue'

import FilterClearButton from '@/components/FilterClearButton.vue'
import HostedApproveModal from '@/components/HostedApproveModal.vue'
import ModulePicker from '@/components/ModulePicker.vue'
import PolicyEditor from '@/components/PolicyEditor.vue'
import QuotaEditor from '@/components/QuotaEditor.vue'
import QuotaSummary from '@/components/QuotaSummary.vue'
import TermField from '@/components/TermField.vue'
import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import { ApiError } from '@/api/client'
import * as ls from '@/api/ls'
import type { LicenseBrief, LicenseReq } from '@/api/ls'
import * as platformsApi from '@/api/platforms'
import {
  BATCH_APPROVE_MAX,
  batchConfirmMessage,
  batchEligible,
  batchIneligibleReason,
  batchResultRows,
  pruneSelection,
  toggleAll,
  toggleSelection,
} from '@/lib/batchApprove'
import type { BatchResultRow } from '@/lib/batchApprove'
import { canActivateDirectly, confirmButtonLabel, maxStrict, policyFor, policyHint } from '@/lib/activation'
import { knownErrorText } from '@/lib/apiErrors'
import { AUTO_APPROVE_ISSUED_VIA, autoReviewReason, isAutoApproved, reviewReasonLabel } from '@/lib/autoApprove'
import { ADDON_FILENAME, LICENSE_FILENAME, copyText, saveTextFile } from '@/lib/download'
import { vEnter } from '@/lib/enterKey'
import { MINUTES_PER_DAY, splitMinutes, totalMinutes } from '@/lib/terms'
import {
  ACTIVATION_POLICY_LABELS,
  activationPolicyLabel,
  defaultTermTotal,
  isInternalPlan,
  issuablePlans,
  planByKey,
  termLabel,
  termOptionsWith,
} from '@/lib/products'
import { planLabel, quotaLabel, quotaSummaryText } from '@/lib/quotas'
import { changedContentKeys, overrideLabels, pickOverride } from '@/lib/requestOverride'
import { seedRequestDraft } from '@/lib/requestDraftSeed'
import { seedQuotas } from '@/lib/seedQuotas'
import { customerOptionLabel } from '@/lib/customerLabel'
import { usePersistedFilters } from '@/lib/persistedFilters'
import { useAuthStore } from '@/stores/auth'
import { useDataStore } from '@/stores/data'
import { useDialogsStore } from '@/stores/dialogs'
import { NO_FILE_DROPPED_MESSAGE, useFileDrop, useWindowFileDrop } from '@/lib/fileDrop'
import { vCardTable } from '@/lib/cardTable'

const store = useDataStore()
const dialogs = useDialogsStore()
const auth = useAuthStore()

/**
 * ★ 憑證設計的競態（沿用批80 批D2 修 presets 時的同一個教訓）：`openDetail` 會用
 * 該申請所屬產品的套裝／模組定義帶入表單。若在 `ensureProducts()` 回來之前就按了
 * 「檢視」，帶入的會是一份空 spec——套裝 chip 亮著卻什麼都沒帶進去。存成 promise，
 * `openDetail` 開頭 await 它。
 */
const productsReady = store.ensureProducts().catch(() => {})

// --- 伺服器端過濾（來源／代理商／產品／狀態籤／只看已退回）--------------------
//
// ★★ 批121 §9：**全部**下推伺服器了。在那之前狀態頁籤與產品是前端桶，於是
//    ①籤上的數字只算「已載入的那幾頁」②在某個籤上按「載入更多」載回的是下一頁
//    原始資料、常常一列都沒增加，看起來像按了沒反應。與授權頁是同一個 bug 的兩個現場。
/**
 * 批155：「來源」下拉多一個選項「平台自動核准」——它不是一種來源，而是「平台送件＋核發方式
 * 是自動核准」（`origin=platform&issued_via=platform`）。放進既有的下拉而不是另開一排膠囊。
 */
const ORIGIN_AUTO_APPROVED = 'platform_auto'
const ORIGINS = ['', 'admin', 'reseller', 'portal', 'site', 'platform', ORIGIN_AUTO_APPROVED]

type Tab = 'draft' | 'pending' | 'approved' | 'rejected' | 'cancelled'
const TABS: { key: Tab; label: string; hint?: string }[] = [
  { key: 'draft', label: '草稿', hint: '內容還可以改；確認送出後才會進入待審核。' },
  { key: 'pending', label: '待審核', hint: '內容已鎖定，等有權限的人審核核發。' },
  { key: 'approved', label: '已核發' },
  { key: 'rejected', label: '已婉拒' },
  { key: 'cancelled', label: '已取消' },
]

/**
 * 批186：下拉、狀態籤與「只看已退回」記在 `lib/persistedFilters`（重新整理、切頁回來都還在）。
 * 籤的預設是進頁時挑的（`defaultTab()`）——記憶裡沒有籤才自動挑，見 `onMounted`。
 */
const pf = usePersistedFilters(
  'requests',
  { originFilter: '', resellerFilter: '', platformFilter: '', listProduct: '', tab: 'pending' as Tab, onlyReturned: false },
  { oneOf: { originFilter: ORIGINS, tab: TABS.map((t) => t.key) } },
)
/**
 * `platformFilter`＝批141：申請人是這個多租戶平台的組織（與「來源」正交：平台組織的單子也可能是我方代建的）。
 * `onlyReturned`＝草稿頁籤內的子篩選：只看被退回的（有 `review.reason` 的草稿）。
 */
const { originFilter, resellerFilter, platformFilter, listProduct, tab, onlyReturned } = pf.refs
const { dirty: filtersDirty } = pf
/** 首次載入完成才讓過濾器的 watch 動作（見 `onMounted` 的順序說明）。 */
const ready = ref(false)
/** 下拉過濾（來源／代理商／產品）——計數與清單都吃這一組。 */
function baseFilter() {
  const auto = originFilter.value === ORIGIN_AUTO_APPROVED
  return {
    origin: (auto ? 'platform' : originFilter.value) || undefined,
    resellerId: resellerFilter.value || undefined,
    platformId: platformFilter.value || undefined,
    product: listProduct.value || undefined,
    issuedVia: auto ? AUTO_APPROVE_ISSUED_VIA : undefined,
  }
}
/**
 * 重載清單（帶目前的籤）＋刷新計數。
 *
 * ★ 計數另外一支端點算全表——分頁天生看不到全貌。兩者平行發，計數慢一點也不擋清單。
 * ★ `onlyReturned` 只在草稿籤有意義（「已退回」不是狀態，`return` 打回 draft）；
 *   在別的籤上送出去會把整頁篩空，所以這裡明確只在 draft 籤帶。
 */
function reloadList() {
  return store.loadRequests({
    ...baseFilter(),
    status: tab.value,
    onlyReturned: tab.value === 'draft' && onlyReturned.value ? true : undefined,
  })
}
function reload() {
  return Promise.all([reloadList(), store.loadRequestCounts(baseFilter())])
}

/**
 * keyset 分頁（批93-D LS-4）：後端一頁 50 筆，`store.requestsNextCursor` 非 null＝還有。
 * 批121 起翻的是**同一個籤**的下一頁。
 */
const loadingMore = ref(false)
async function loadMore() {
  if (!store.requestsNextCursor || loadingMore.value) return
  loadingMore.value = true
  try {
    await store.loadMoreRequests()
  } catch (e) {
    await fail(e, '讀取下一頁失敗')
  } finally {
    loadingMore.value = false
  }
}

onMounted(async () => {
  // ★ 批121：順序有意義——**先**拿全表計數，用它挑預設籤，最後才載那個籤的清單。
  //   反過來會先載一頁「待審核」（常常是空的）、挑到草稿之後再載一次，白跑一趟；
  //   而且計數現在是挑籤的唯一依據（以前是數已載入的那一頁，超過一頁就挑錯）。
  await Promise.all([
    store.ensureCustomers(),
    store.loadRequestCounts(baseFilter()),
    productsReady,
    // 代理商下拉：superadmin 讀得到公司清單；admin 讀不到就退化成用列上帶的名字組選項。
    store.ensureResellers(),
    // 批141：平台下拉。平台清單是營運主管以上的端點（admin 403）→ 讀不到就從列上湊。
    auth.isManagerOrAbove
      ? platformsApi
          .listPlatforms()
          .then((res) => {
            platformList.value = res.items.map((x) => ({ id: x.id, name: x.name }))
          })
          .catch(() => {})
      : Promise.resolve(),
  ])
  // 批186：「平台自動核准」一律在〈已核發〉籤（讀回記憶時 `flush:'sync'` 那支 watch 沒機會跑，這裡補回不變式）。
  const picked: Tab = originFilter.value === ORIGIN_AUTO_APPROVED ? 'approved' : defaultTab()
  pf.setDefaults({ tab: picked })
  if (!pf.restored.has('tab') || originFilter.value === ORIGIN_AUTO_APPROVED) tab.value = picked
  // 「只看已退回」的勾選只在有退回的草稿時才畫——讀回 true 卻沒有退回單，就成了看不見的篩選。
  if (onlyReturned.value && !returnedCount.value) onlyReturned.value = false
  try {
    await reloadList()
  } catch (e) {
    await fail(e, '讀取失敗')
  } finally {
    // watch 在這之前不動作：上面套用預設籤會觸發它，那一次是多餘的往返。
    ready.value = true
  }
})

async function fail(e: unknown, fallback: string) {
  await dialogs.notify({ title: '操作失敗', message: errText(e, fallback) })
}
/** 後端錯誤碼 → 給操作者看的中文。 */
const ERRORS: Record<string, string> = {
  // 批113：`activation_requires_approval`／`superadmin_required` **不放這張表**——後端的 message 已印出
  // 政策實際要求的中文角色名（批110 起多半是「營運主管」），寫死在這裡會像走查抓到的
  // 「需要超級管理者審核」一樣說錯人；讓 `errText` 落到 `obj.message`。
  not_owner: '這是代理商的草稿，我方不能代改內容——請「退回草稿」並附上要改什麼。',
  already_reviewed: '這筆已經處理完成了（可能剛被另一位處理）。請重新整理看最新狀態。',
  product_mismatch: '下拉選的產品與 request.json 內寫的不一致——請確認選對了客戶與檔案。',
  // 批114（B8 裁示 ㉚）：系統不再有「預設產品」的靜默兜底——沒帶產品一律擋下。
  product_required: '這份 request.json 沒有寫產品——請在上面的「產品」下拉選要核發哪一個。',
  unknown_product: 'request.json 指定的產品不存在或已停售，請先到〈產品〉建立／啟用它。',
  product_deprecated: 'request.json 指定的產品已停售，請先到〈產品〉把它改回販售中。',
  unknown_plan: '選的套裝不屬於這個產品。',
  hosted_instance: '這份檔案指向我方代管的站台——代管站的授權由中台派送，不能走這條路。',
  pending_exists: '這台主機已經有一筆待審核的申請，請先處理原申請。',
  // 批97：撤回不分來源，但撤別人的單子要附理由；退回只對「有草稿階段」的來源有意義。
  reason_required: '撤回別人送出的申請必須附上理由（對方看得到）。',
  no_draft_stage:
    '客戶自助／站台／平台來源的申請沒有草稿階段，無法退回——內容要調整請直接在下方修改後核發（需要營運主管以上），或用「婉拒」並附理由請對方重新送出。',
  // 批121：帶了內容欄位但身分不到營運主管（照原內容核發不受影響）。
  role_required: '審核時修改內容需要「營運主管」以上的身分；照原申請內容核發則不受限制。',
}
/**
 * 配額類的 422（批114）：`{code, reason, key, label}`——哪個鍵不合法由**憑證設計**決定，
 * 所以訊息要用回應帶回來的 `label` 組字（前端一個鍵名都不認得）。
 */
const QUOTA_REASONS: Record<string, string> = {
  quota_required: '請在下方「數量配額」填寫「%s」——這一項是必填。',
  quota_not_nullable: '「%s」不可以勾「無上限」，請填一個數字。',
}
function errText(e: unknown, fallback: string): string {
  const d = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  if (d == null) return fallback
  if (typeof d === 'string') return ERRORS[d] ?? d
  const obj = d as { code?: string; message?: string; reason?: string; label?: string; key?: string }
  const reason = obj.reason && QUOTA_REASONS[obj.reason]
  if (reason) return reason.replace('%s', obj.label || obj.key || '')
  if (obj.code && ERRORS[obj.code]) return ERRORS[obj.code]
  // 批155：內部套裝／模組、未知模組等跨畫面共用的碼。
  const known = knownErrorText(obj)
  if (known) return known
  if (obj.message) return obj.message
  return obj.code ?? JSON.stringify(d)
}

// --- 頁籤（狀態）------------------------------------------------------------
// （`Tab`／`TABS` 與籤的 ref 在檔案上方——批186 記憶要在 watch 註冊之前建好。）
// ★ 批121：三個過濾器與狀態籤現在都是**伺服器端**的，換任何一個都要重載。
// 批155：平台自動核准的申請一律是「已核發」——選了這個過濾就直接切到那一籤（其他籤必然是空的）。
// ★ `flush: 'sync'`：在下面那支 watch 跑之前就把籤換好，兩個值一起變只重載一次
//   （不同步的話會先載一次「待審核」再載「已核發」，兩個請求誰先回來誰贏）。
watch(
  originFilter,
  (v) => {
    if (v === ORIGIN_AUTO_APPROVED && tab.value !== 'approved') tab.value = 'approved'
  },
  { flush: 'sync' },
)
watch([originFilter, resellerFilter, platformFilter, listProduct, tab, onlyReturned], () => {
  if (ready.value) void reload()
})

/** 舊列沒有 `origin`（批93-C 之前的資料）＝視同客戶自助 portal 送的。 */
function originOf(r: LicenseReq): string {
  return r.origin || 'portal'
}
const ORIGIN_LABELS: Record<string, string> = {
  portal: '客戶自助',
  site: '站台',
  reseller: '代理商',
  admin: '我方',
  // 批141：合作平台以機器身分代送（平台 API）。
  platform: '平台',
}
function originLabel(r: LicenseReq): string {
  const o = originOf(r)
  if (o === 'reseller') return r.reseller_name ? `代理商・${r.reseller_name}` : '代理商'
  if (o === 'platform') return r.platform?.name ? `平台・${r.platform.name}` : '平台'
  return ORIGIN_LABELS[o] ?? o
}
/**
 * 沒有草稿階段的來源（批141 裁示 27：平台來源也不開放退回補件）——`return` 會 409
 * `no_draft_stage`，所以「退回草稿」鈕對它們不出現。
 */
const NO_DRAFT_ORIGINS = ['portal', 'site', 'platform']
/** 我方草稿（`origin='admin'`）才可以就地改內容；其餘草稿唯讀。 */
function isOurDraft(r: LicenseReq): boolean {
  return r.status === 'draft' && originOf(r) === 'admin'
}
function reviewReason(r: LicenseReq): string | null {
  if (r.review?.reason) return String(r.review.reason)
  const review = (r.request_json?.review ?? null) as { reason?: string | null } | null
  return review?.reason ? String(review.reason) : null
}

/**
 * 籤上的數字＝**伺服器算的全表筆數**（批121 §9）。
 *
 * ★★ 以前是把 `store.requests`（＝已載入的那幾頁）逐列分桶累加，於是申請單一超過
 *    一頁就開始少算——而審核者正是靠「待審核 N」決定今天要不要處理，漏掉一筆的
 *    代價是客戶乾等。分頁天生看不到全貌，計數只能問伺服器。
 * ★ 還沒拿到（首次載入中／計數端點失敗）時退回已載入的筆數——寧可顯示「至少這麼多」
 *   也不要空白跳動。
 */
const counts = computed<Record<Tab, number>>(() => {
  const c = store.requestCounts
  const n = store.requests.length
  if (!c) return { draft: n, pending: n, approved: n, rejected: n, cancelled: n }
  return {
    draft: c.draft,
    pending: c.pending,
    approved: c.approved,
    rejected: c.rejected,
    cancelled: c.cancelled,
  }
})
const returnedCount = computed(() => store.requestCounts?.returned ?? 0)
/**
 * 進頁時停在**第一個非空**的頁籤（批113 走查第 10 點）。
 *
 * 原本寫死 `pending`：沒有待審核、但草稿有兩筆時，畫面說「這個分類目前沒有申請」，
 * 而人明明剛在客戶頁建好草稿——看起來像草稿沒建成功。
 * ★ 只在載入完成後跑**一次**：之後的 reload（核發／退回）不該把操作者的頁籤搶走。
 * ★ 批121：判準改讀**全表**計數（`loadRequestCounts` 先回來），所以第 51 筆之後
 *   才有草稿的站也挑得對。
 * ★ 批186：只回傳結果——記憶裡有籤就不套用（`onMounted`），挑到的籤同時是這頁「清除」的目標。
 */
function defaultTab(): Tab {
  // 待審核優先：有東西等人核發時審核者一進來就該看到；沒有才退到草稿、再退到已核發。
  // （批113 走查第 10 點的原症狀是 pending=0 時停在空的待審核分類，草稿有東西卻看不到。）
  const order: Tab[] = ['pending', 'draft', 'approved']
  return order.find((t) => counts.value[t] > 0) ?? 'pending'
}
// ★ 批121：**不再前端過濾**——`store.requests` 已經是伺服器依 `status`／`product`／
//   `only_returned` 篩過的那一頁。留著再篩一次會把「載入更多」載回來的列吃掉。
const visibleRequests = computed(() => store.requests)

// --- 批141：平台下拉 ----------------------------------------------------------
const platformList = ref<{ id: string; name: string }[]>([])
/** 平台下拉的選項：營運主管以上讀得到平台清單；讀不到（admin）就從列上帶的平台湊。 */
const platformOptions = computed<{ id: string; name: string }[]>(() => {
  if (platformList.value.length) return platformList.value
  const seen = new Map<string, string>()
  for (const r of store.requests) if (r.platform) seen.set(r.platform.id, r.platform.name)
  if (platformFilter.value && !seen.has(platformFilter.value)) seen.set(platformFilter.value, '（目前篩選的平台）')
  return [...seen].map(([id, name]) => ({ id, name }))
})

// --- 批141：批次核准（裁示 30／40）--------------------------------------------
//
// 只在〈待審核〉籤出現；代管開站申請不給勾（要選主機並開站）。照**原內容**簽發、逐筆套
// 政策，一筆一交易——結果逐筆列出（成功帶授權編號、失敗帶原因）。
const canBatch = computed(() => tab.value === 'pending' && auth.isAdminOrAbove)
const picked = ref<string[]>([])
const batchBusy = ref(false)
const batchRows = ref<BatchResultRow[]>([])
const batchOpen = ref(false)
const pickableRows = computed(() => visibleRequests.value.filter(batchEligible))
const allPicked = computed(
  () => pickableRows.value.length > 0 && pickableRows.value.every((r) => picked.value.includes(r.id)),
)
// 清單一換（換籤、重載、載入更多、別人處理掉某一筆）就把不在畫面上的勾選拿掉。
watch(visibleRequests, (rows) => {
  picked.value = pruneSelection(picked.value, rows)
})
function togglePick(id: string) {
  picked.value = toggleSelection(picked.value, id)
}
function togglePickAll() {
  picked.value = toggleAll(picked.value, visibleRequests.value)
}
async function runBatch() {
  const ids = [...picked.value]
  if (!ids.length || ids.length > BATCH_APPROVE_MAX) return
  const ok = await dialogs.confirm({
    title: `批次核准 ${ids.length} 筆`,
    message: batchConfirmMessage(ids.length),
    confirmText: '核准',
  })
  if (!ok) return
  // 客戶名先記下來：核准之後這些列會離開〈待審核〉籤，重載後就查不到了。
  const names = new Map(
    visibleRequests.value.filter((r) => ids.includes(r.id)).map((r) => [r.id, store.customerName(r.customer_id)]),
  )
  batchBusy.value = true
  try {
    const res = await ls.batchApproveLicenseRequests(ids)
    batchRows.value = batchResultRows(ids, res, (id) => names.get(id) ?? '—')
    picked.value = []
    batchOpen.value = true
    await reload()
  } catch (e) {
    await fail(e, '批次核准失敗')
  } finally {
    batchBusy.value = false
  }
}
const batchOkCount = computed(() => batchRows.value.filter((r) => r.ok).length)
const batchFailCount = computed(() => batchRows.value.length - batchOkCount.value)
/**
 * 代理商下拉的選項：優先用 store 的公司清單，讀不到（admin 403）就從列上湊。
 * ★ 批186：記住的代理商不在選項裡（列上湊不到、或公司已刪）時補一個占位——否則下拉整個藏起來
 *   （`v-if="resellerOptions.length"`）、條件卻還在篩，成了看不見的過濾（同平台下拉的作法）。
 */
const resellerOptions = computed<{ id: string; name: string }[]>(() => {
  const seen = new Map<string, string>()
  if (store.resellers.length) for (const r of store.resellers) seen.set(r.id, r.name)
  else {
    for (const r of store.requests) {
      if (r.reseller_id) seen.set(r.reseller_id, r.reseller_name ?? r.reseller_id.slice(0, 8))
    }
  }
  if (resellerFilter.value && !seen.has(resellerFilter.value)) seen.set(resellerFilter.value, '（目前篩選的代理商）')
  return [...seen].map(([id, name]) => ({ id, name }))
})

// --- 詳情／編輯對話框 --------------------------------------------------------
const approving = ref<LicenseReq | null>(null)
/** 進行中的動作名（同時 disable 全部按鈕，避免連點兩次核發兩張證）。 */
const busy = ref<string | null>(null)

const approveSpec = computed(() => store.productByKey(approving.value?.product))
/** 期限下拉的選項（**總分鐘**，批108）＋把當前值併進去（見 `termOptionsWith`）。 */
const approveTermOptions = computed(() => termOptionsWith(approveSpec.value, form.value.term_total))
/**
 * 批155：內部套裝只有營運主管以上看得到、選得到；申請內容（或現行證）本來就是內部套裝時照列
 * 但停用（`planLocked`），否則 chip 一個都不亮、看不出現在是什麼。
 */
const approvePlans = computed(() =>
  issuablePlans(approveSpec.value, { internal: auth.isManagerOrAbove, keep: approvePlan.value || null }),
)
function planLocked(p: { internal?: boolean }): boolean {
  return isInternalPlan(p) && !auth.isManagerOrAbove
}

// 內容表單（只有我方草稿才可編輯，其餘唯讀顯示同一份值）
/**
 * `term_total`＝期限的**總分鐘**（批108）；送出前用 `splitMinutes` 拆成兩欄。
 *
 * 批113（#5521）：多了啟用時間與到期方式／到期時刻三欄——它們與客戶頁的直簽表單
 * （`CustomersView.issue`）**同名同語意**，兩邊的算法要對得起來。
 * `not_before`／`expires_at` 存的是 `datetime-local` 的**本地時間字串**，送出前轉 ISO。
 */
const form = ref({
  term_total: 365 * MINUTES_PER_DAY,
  note: '',
  not_before: '',
  expiry_mode: 'on_fetch' as 'on_fetch' | 'at',
  expires_at: '',
})
const approveModules = ref<Record<string, string>>({})
const approvePlan = ref<string>('')
const approveQuotas = ref<Record<string, number | null> | null>(null)
/** 批113（#5520）：行為參數（`PolicyEditor` 的 v-model；空物件＝一個鍵都不帶）。 */
const approvePolicyValues = ref<Record<string, unknown>>({})
/**
 * 配額編輯器的必填檢查（批114）：憑證設計標了 `required` 的鍵有沒有填。
 *
 * ★ 起手值是 `true`：判斷權在 `QuotaEditor`（它才知道哪些鍵必填），這裡只是接收訊號。
 *   起手設 false 的話，訊號還沒到之前送出鈕會無故反灰。
 */
const quotasValid = ref(true)
const pickerKey = ref(0)
/**
 * 開窗當下的內容快照（批121）——核發時用它比出「審核者改了哪幾格」。
 * `null`＝還沒開窗／還沒拍（拍攝時機見 `openDetail` 末尾的 `nextTick`）。
 */
const baseline = ref<Record<string, unknown> | null>(null)

/** 改**草稿內容**（所有權制：我方自建的草稿才行，見檔頭 ③）。 */
const draftEditable = computed(
  () => !!approving.value && isOurDraft(approving.value) && auth.isAdminOrAbove,
)
const isDraft = computed(() => approving.value?.status === 'draft')
const isPending = computed(() => approving.value?.status === 'pending')
/**
 * 批121：**待審核的申請，營運主管以上可以在核發當下改內容**。
 *
 * 為什麼加這條路：`origin ∈ {portal, site}`（客戶自助、CS 站台送件）沒有草稿階段，
 * `return` 會 409 `no_draft_stage` ⇒ 審核者看到內容不對只能婉拒請對方重送，或繞去直簽
 * （權限更大、留痕更少）。改完的內容只影響這一次簽發：`request_json`（送件方交來的證據檔）
 * 一個字都不動，改了什麼記在稽核的 `overrides`。
 * ★ 能不能真的按下「核發」仍然由**政策**決定，而且政策是拿**改完的內容**重算的
 *   （後端 `_assert_can_approve`）——改成偏離套裝模板就會升身分，站台來源的申請則因為
 *   沒有政策快照而一律需要超管。這個 computed 只管「編輯器開不開」。
 */
const canOverride = computed(() => isPending.value && auth.isManagerOrAbove)
/** 表單能不能動（改草稿 或 核發前覆寫）。 */
const editable = computed(() => draftEditable.value || canOverride.value)
/**
 * 可否**作廢**這筆申請（批97）。
 *
 * ★ 刻意與 `editable` 分開：`editable` 是「可以改內容」（我方自建草稿才行，所有權制），
 *   `canCancel` 是「可以作廢」——那是我方的最終仲裁權，任何來源的 draft／pending 都行。
 *   兩者混用會回到批97 之前的死鎖（別人送的單子沒有任何出口）。
 */
const canCancel = computed(() => (isDraft.value || isPending.value) && auth.isAdminOrAbove)
/** 撤回別人送出的單子＝理由必填（對方看得到）；我方自建草稿選填。 */
const cancelReasonRequired = computed(
  () => !!approving.value && originOf(approving.value) !== 'admin',
)

/**
 * 目前內容（政策判定與送出都吃這一份）。
 *
 * ★ 批114：**數量一律只住 `quotas`**。原本另有兩個獨立數字欄，同一個數字有兩個輸入框，
 *   而且獨立欄會靜默蓋掉配額表裡填的值（B5 查到的 bug）。政策判定那一側
 *   （`lib/activation.ts::contentMatchesPlan`）已經改成「一級鍵缺席時看 `quotas` 同名鍵」，
 *   所以這裡只送配額包就夠。
 */
const content = computed(() => ({
  modules: approveModules.value,
  quotas: approveQuotas.value,
  term_days: splitMinutes(form.value.term_total)[0],
  term_minutes: splitMinutes(form.value.term_total)[1],
}))
/** 草稿：即時算；pending：快照與現行政策取**較嚴**者（收緊立即生效、放寬不追溯）。 */
const policy = computed(() => {
  const live = policyFor(approveSpec.value, approvePlan.value || null, content.value)
  if (!isPending.value) return live
  return { policy: maxStrict(approving.value?.activation_policy, live.policy), reason: live.reason }
})
const canDirect = computed(() => canActivateDirectly(auth.role, policy.value.policy))
/**
 * 政策提示。★ `policyHint` 的措辭是寫給**草稿**看的（「送出即當場簽發」），pending 沒有
 * 「送出」這個動作——那裡的動作是「核發」。批121 讓 pending 也能改內容之後這句更常被看到，
 * 所以兩種狀態分開講。
 */
const policyText = computed(() => {
  if (!isPending.value) return policyHint(policy.value, canDirect.value)
  if (canDirect.value) {
    return dirtyKeys.value.length
      ? '你的身分足以核發這份（已調整過的）內容。'
      : '你的身分足以核發這份內容。'
  }
  return policy.value.reason === 'custom_content'
    ? '這份內容偏離了套裝模板，需要更高的身分才能核發。'
    : '這份內容需要更高的身分才能核發。'
})
/**
 * 送件的是自己。★ 批108 起**這不再是阻擋條件**（那道 409 已拿掉——我方只有一位
 * superadmin 時，自己擬的申請永遠核發不了）。留著只為了在詳情頁出一行中性提示：
 * 核發後稽核的 `self_approved` 會是 true。
 */
const isSelfMaker = computed(
  () => !!approving.value?.requested_by && approving.value.requested_by === auth.email,
)
/**
 * 必填配額缺值（批114 取代批106 的「數量必填」特例判斷）。
 *
 * 後端核發會回 422 `{code:'quotas_invalid', reason:'quota_required'}`——哪些鍵必填由
 * **憑證設計**宣告（`QuotaSpec.required`），LS 程式裡不再有任何一個具體鍵名。訊號來自
 * `QuotaEditor` 的 `update:valid`：它才知道每個鍵的旗標與當下的輸入值。
 * ★ pending（唯讀）不吃這條：內容已經鎖定，能不能核發由後端說了算——這裡只是不要讓
 *   操作者按下去才看到錯誤。
 */
const quotaMissing = computed(() => !!approving.value && !quotasValid.value)
/**
 * 批121：審核者在這個視窗裡改掉的內容欄位（相對於開窗快照）。
 *
 * 空陣列＝照申請內容核發（行為與批121 之前逐位元相同）；非空時核發鈕改口、送出前先確認，
 * body 也只帶這幾個鍵——整包送會讓「什麼都沒動」也被記成覆寫，還會讓政策拿展開後的值重算。
 */
const dirtyKeys = computed(() =>
  canOverride.value && baseline.value ? changedContentKeys(baseline.value, draftBody()) : [],
)
/**
 * 這筆申請「有現行證，卻沒帶完整配額包」（批121）。
 *
 * 舊版 CS 站台送件只有 `seats_requested`，核發時 LS 的 `kind="new"` 不沿用現行證 ⇒
 * 未列出的配額掉回套裝模板或缺席預設，而協力夥伴席次缺席在產品端＝0——這就是主人回報
 * 「無上限的協作席次重新申請變成 0」的來源。新版站台改送整包後這個提示就不會出現。
 */
const missingQuotaPackage = computed(() => {
  const r = approving.value
  if (!r || !isPending.value || (r.kind ?? 'new') !== 'new' || !r.current) return false
  const raw = (r.request_json?.requested ?? {}) as Record<string, unknown>
  return !r.quotas_requested && !raw.quotas
})
const canApproveNow = computed(
  // 批108：`isSelfMaker` 不再進這條判斷（自己送的自己也能核發）。
  () => isPending.value && canDirect.value && !quotaMissing.value,
)

/**
 * `kind='change'` 的變更申請：**申請單沒列到的項目一律沿用現行證**（後端
 * `services/license_request.activate_request` 的 `change` fallback 鏈）。
 *
 * ★ 為什麼要在畫面上講：portal 的變更申請只送客戶改到的那幾個配額鍵（模組明確給 `{}`、
 *   `plan` 給 `null`），開通框卻會把現行證的模組／配額預帶進來顯示。不寫這一句的話，審核者
 *   會以為「這些是客戶要求的內容」，其實是「維持現狀」——兩者在畫面上長得一模一樣，
 *   但語意差很多（例如客戶其實想砍掉某個模組時，這裡看不出來他沒提）。
 */
const carriedOver = computed<string[]>(() => {
  const r = approving.value
  if (!r || r.kind !== 'change' || !r.current) return []
  const reqd = (r.request_json?.requested ?? {}) as Record<string, unknown>
  const out: string[] = []
  if (!r.plan && !reqd.plan) out.push('套裝')
  const mods = r.modules_requested ?? (reqd.modules as Record<string, string> | undefined)
  if (!mods || !Object.keys(mods).length) out.push('模組')
  // 批114：配額不再逐欄硬編。申請內容完全沒提配額＝整包沿用；只提了幾個鍵時，列出
  // **沒被提到的那幾個鍵的中文名**（名稱一律查憑證設計，不寫死任何鍵）。
  const asked = seedQuotas(approveSpec.value, {
    ...reqd,
    quotas: r.quotas_requested ?? reqd.quotas ?? null,
  })
  if (asked == null) out.push('配額')
  else {
    const rest = (approveSpec.value?.quotas ?? [])
      .filter((q) => !q.deprecated && !(q.key in asked))
      .map((q) => quotaLabel(approveSpec.value, q.key))
    if (rest.length) out.push(...rest)
  }
  if (r.term_days == null && reqd.term_days == null) out.push('期限（沿用原到期日）')
  return out
})

async function openDetail(r: LicenseReq) {
  rejectOpen.value = false
  returnOpen.value = false
  commentOpen.value = false
  cancelOpen.value = false
  rejectReason.value = ''
  returnReason.value = ''
  cancelReason.value = ''
  commentText.value = ''
  reviewNote.value = ''
  if (r.kind === 'hosted_provision') {
    hostedApproving.value = r
    return
  }
  await productsReady // 不 await 會靜默漏帶套裝內容
  const spec = store.productByKey(r.product)
  const reqd = (r.request_json?.requested ?? {}) as Record<string, any>
  /**
   * 批113：`policy`／`not_before`／`expires_at` 三鍵的 **raw fallback 只對我方草稿開放**
   * ——與後端 `services/license_request.requested_content()` 的讀端閘逐字對稱。
   * ★ 客戶自己在 `request.json` 裡塞的 `expires_at` 後端一律忽略；照著顯示的話，
   *   審核者會以為簽出來的證是那個到期日（走查第 2 點的同一種錯：比空白更糟）。
   */
  const rawStaff: Record<string, any> = originOf(r) === 'admin' ? reqd : {}
  const cur: LicenseBrief | null = r.current ?? null
  // 期限的 fallback 鏈**要與後端 `activate_request` 的 `_pick` 同序**：
  // 申請內容 → 套裝模板 → 現行證（change）→ 產品預設。
  // ★ 少了「套裝模板」那一段的下場：代理商送了 90 天的試用但沒填期限，這裡會顯示
  //   產品清單的第一個值（1 個月），開通後實際簽出 3 個月——審核者看到的數字不是
  //   他按下去會發生的事，而且畫面上沒有任何地方看得出差異。
  // ★ 批108：一律換算成**總分鐘**再比較／帶入。`?? `（nullish）在這裡是對的——
  //   `term_days` 是 0 的分鐘級內容不可以被當成「沒給」跳過。
  const planRow = planByKey(spec, r.plan ?? cur?.plan ?? null)
  const planTerm =
    planRow && (planRow.term_days != null || planRow.term_minutes)
      ? totalMinutes(planRow.term_days, planRow.term_minutes)
      : null
  const fromReq =
    r.term_days != null || r.term_minutes != null
      ? totalMinutes(r.term_days, r.term_minutes)
      : reqd.term_days != null || reqd.term_minutes != null
        ? totalMinutes(reqd.term_days as number, reqd.term_minutes as number)
        : null
  const fromCur =
    cur && (cur.term_days != null || cur.term_minutes)
      ? totalMinutes(cur.term_days, cur.term_minutes)
      : null
  const term = fromReq ?? planTerm ?? fromCur
  form.value = {
    // 不在產品清單裡的天數（試用 90）照樣顯示——`approveTermOptions` 會把它併進選項。
    // 最後一段是產品的 `default_term_days`（`defaultTermTotal`），不是清單第一個值。
    term_total: term ?? defaultTermTotal(spec),
    note: r.note ?? '',
    // 批113（#5521）：申請內容 → request_json（後端已過閘的投影優先）。
    // 現行證的 `not_before` **刻意不當 fallback**——那是上一張證的合約起算日，
    // 帶進新申請等於替客戶做了他沒提的決定。
    not_before: toLocalInput((r.not_before ?? rawStaff.not_before) as string | null | undefined),
    expiry_mode: (r.expires_at ?? rawStaff.expires_at) ? 'at' : 'on_fetch',
    expires_at: toLocalInput((r.expires_at ?? rawStaff.expires_at) as string | null | undefined),
  }
  approvePlan.value = r.plan ?? cur?.plan ?? ''
  /**
   * 模組／配額／行為參數的起手值（批114；#5539／#5538）。
   *
   * ★ 取值鏈抽在 `lib/requestDraftSeed.ts`，**與後端 `activate_request` 的 `_pick`
   *   逐段對齊**（`new`：申請內容 → 套裝；`change`：再加現行證；`renew`：申請內容 →
   *   現行證，沒有套裝那一段）。這裡原本只有期限有套裝 fallback，其餘欄位在「沒有現行證」
   *   的情境下全部落空 → `QuotaEditor` 以 `absent_default`（0）起手並回寫表單 →
   *   一按儲存就把全 0 存進草稿（#5539）。
   * ★ `rawStaff` 已經過 origin 讀端閘（只有我方草稿讀得出 policy／時刻三鍵）。
   */
  const seed = seedRequestDraft({
    kind: r.kind,
    spec,
    request: r,
    raw: { ...reqd, policy: rawStaff.policy },
    plan: planByKey(spec, approvePlan.value || null),
    current: cur,
  })
  approveModules.value = seed.modules
  approveQuotas.value = seed.quotas
  approvePolicyValues.value = seed.policy
  pickerKey.value++
  approving.value = r
  // 批121：快照「開窗當下的內容」當比較基準——核發時只送真的被改過的鍵。
  // ★ 一定要等 `nextTick`：`QuotaEditor` 的 `onMounted(emitAll)` 會回寫一次 `approveQuotas`
  //   （把缺席鍵補成 `absent_default`），在那之前拍的快照一定與表單不同 ⇒ 什麼都沒動
  //   也會被當成「改過」，稽核塞滿雜訊、政策還會拿展開後的值重算。
  await nextTick()
  baseline.value = draftBody()
}

/** 選套裝＝整包帶入模板（改任一項就會讓政策升為 superadmin，畫面會即時說）。 */
function applyPlan(key: string) {
  approvePlan.value = key
  const p = approvePlans.value.find((x) => x.key === key)
  if (!p) return
  approveModules.value = { ...p.modules }
  // 批114：模板的相容數字欄也要併進配額包（舊套裝把數量存在一級欄位上，只讀 `quotas`
  // 會讓那些數字在套用模板時憑空消失）。
  approveQuotas.value = seedQuotas(approveSpec.value, p)
  // 套裝有指定天數就照套裝（下拉會自己把這個值補進選項——見 `termOptionsWith`）。
  // ★ 舊寫法是「只在產品的期限清單裡才套用」，於是選了 90 天的試用會留著上一個值
  //   365，內容就此偏離模板、政策被升成 superadmin——按鈕寫「確認並核發」卻進了待審。
  if (p.term_days != null || p.term_minutes) {
    form.value.term_total = totalMinutes(p.term_days, p.term_minutes)
  }
  // 批113（#5520）：行為參數也跟著套裝走（與 `CustomersView.applyPlan` 逐字對稱）。
  approvePolicyValues.value = p.policies ? { ...p.policies } : {}
  pickerKey.value++
}

/**
 * ISO（UTC）→ `datetime-local` 欄位吃的**本地時間**字串（`YYYY-MM-DDTHH:mm`）。
 * ★ 不能直接 `iso.slice(0, 16)`——那是 UTC，台灣會少 8 小時，而且畫面上看不出來。
 */
function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
}
/** `datetime-local` 的本地時間字串 → ISO（空字串＝沒指定＝null）。 */
function toIso(local: string): string | null {
  if (!local) return null
  const d = new Date(local)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

function draftBody() {
  return {
    plan: approvePlan.value || null,
    modules: approveModules.value,
    quotas: approveQuotas.value,
    term_days: content.value.term_days,
    term_minutes: content.value.term_minutes,
    note: form.value.note.trim() || null,
    // 批113（#5520／#5521）：與 `CustomersView.draftRequestJson()` 逐字對稱——
    // 同一份草稿在兩個畫面上改，送出去的內容必須是同一種形狀。
    // ★★ 批114（#5538）：**空物件不可再壓成 `null`**。`{}`＝「一個政策都不帶」是使用者
    //    明確的選擇，`null`＝「沒指定，核發時從套裝模板展開」——壓成 null 的下場就是
    //    把勾選全部取消之後，簽出來的證仍然帶著套裝預設值，而畫面上看不出任何異常。
    policy: { ...approvePolicyValues.value },
    not_before: toIso(form.value.not_before),
    expires_at: form.value.expiry_mode === 'at' ? toIso(form.value.expires_at) : null,
  }
}

async function saveDraft(): Promise<boolean> {
  const r = approving.value
  if (!r || !draftEditable.value) return false
  busy.value = 'save'
  try {
    await ls.updateLicenseRequest(r.id, draftBody())
    await reload()
    return true
  } catch (e) {
    await fail(e, '儲存草稿失敗')
    return false
  } finally {
    busy.value = null
  }
}
async function saveOnly() {
  if (await saveDraft()) approving.value = null
}

/** 確認送出（政策放行時後端會當場簽發，所以按鈕文字要事先分流）。 */
async function confirmDraft() {
  const r = approving.value
  if (!r) return
  const ok = await dialogs.confirm({
    title: canDirect.value ? '確認並核發' : '確認送出',
    message: canDirect.value
      ? '這份內容依套裝政策可免審核發——按下確認會「當場簽發」一張授權。要繼續嗎？'
      : '確認送出後內容就鎖定了，要改必須退回草稿。要繼續嗎？',
    confirmText: canDirect.value ? '確認並核發' : '確認送出',
  })
  if (!ok) return
  busy.value = 'confirm'
  try {
    if (draftEditable.value) await ls.updateLicenseRequest(r.id, draftBody())
    const res = await ls.confirmLicenseRequest(r.id)
    approving.value = null
    await reload()
    if (res.status === 'approved') {
      // 批113：與 `approve()` 共用同一則結果通知（短碼＋啟用時間已過提示＋下載捷徑）。
      await announceIssued(
        res.issued_license_id,
        !!res.not_before_elapsed,
        '依套裝政策當場簽發——',
      )
    } else {
      await dialogs.notify({
        title: '已送出',
        message:
          res.policy_reason === 'custom_content'
            ? `內容與套裝模板不同，政策升為「${ACTIVATION_POLICY_LABELS.superadmin}」——已進入「待審核」。`
            : '已進入「待審核」，等有權限的人審核核發。',
      })
    }
  } catch (e) {
    await fail(e, '確認送出失敗')
  } finally {
    busy.value = null
  }
}

// --- 審核核發／退回／婉拒／撤回 -----------------------------------------------
const reviewNote = ref('')
const returnOpen = ref(false)
const returnReason = ref('')
const rejectOpen = ref(false)
const rejectReason = ref('')
const cancelOpen = ref(false)
const cancelReason = ref('')
const commentOpen = ref(false)
const commentText = ref('')
// 核發回應帶回的未知鍵（警告級，不擋核發）。
const unknownModules = ref<string[]>([])
const unknownQuotaKeys = ref<string[]>([])
const unknownModuleItems = ref<string[]>([])

/**
 * 核發成功後的結果通知（批113 走查第 8 點）。
 *
 * 在這之前核發完 modal 直接關回清單，畫面上**什麼都沒說**——營運主管按完不知道成功了沒，
 * 也不知道證在哪裡。這裡一次交代三件事：簽出哪一張、啟用時間有沒有已經過去、要不要現在帶走。
 * ★ `dialogs` 只有 confirm／notify 兩種，所以「附下載動作」＝用 confirm 當二選一。
 * ★ 加購（`kind='addon'`）核發的是子證，`issued_license_id` 是 null → 只通知不給下載捷徑
 *   （子證要從申請詳情的取證區拿，那裡才有 `issued_addon_id`）。
 */
async function announceIssued(
  licenseId: string | null | undefined,
  elapsed: boolean,
  lead: string,
  extra = '',
): Promise<void> {
  const title = elapsed ? '已核發（啟用時間已過）' : '已核發'
  const note = elapsed
    ? '\n\n⚠ 草稿裡指定的「啟用時間」在核發當下已經過去——這張證立刻生效，' +
      '期限從現在起算，不是從那個時間點。'
    : ''
  const head = licenseId ? `${lead}授權編號 ${licenseId.slice(0, 8)}。` : lead
  if (!licenseId) {
    await dialogs.notify({ title, message: head + note + extra })
    return
  }
  const ok = await dialogs.confirm({
    title,
    message: `${head}${note}${extra}\n\n要現在下載授權檔交給客戶嗎？`,
    confirmText: '立即下載',
    cancelText: '稍後',
  })
  if (ok) await downloadLicenseById(licenseId)
}

/** 依 id 取證存檔（申請已從 `approving` 清掉，所以不能走 `downloadIssued`）。 */
async function downloadLicenseById(licenseId: string) {
  try {
    const res = await ls.licenseFile(licenseId)
    saveTextFile(res.blob, LICENSE_FILENAME)
    await dialogs.notify({
      title: '已下載憑證',
      message: `已存成 ${LICENSE_FILENAME}。交給客戶放進站台的 license/ 目錄後重啟，或請他貼進站台〈系統 → 授權 → 手動安裝 .lic〉。`,
    })
  } catch (e) {
    // 證已經簽出去了，取原文失敗只是這一次沒帶走——別讓人以為核發失敗。
    await fail(e, '取得憑證失敗（授權已核發，可到〈已核發〉頁籤的詳情再下載一次）')
  }
}

async function approve() {
  const r = approving.value
  if (!r || !canApproveNow.value) return
  // 批121：改過內容就先確認一次——「核發」與「改完再核發」是兩件事，按鈕上看得到、
  // 按下去之前也要再說一次改了什麼（稽核會逐欄留痕）。
  const dirty = dirtyKeys.value
  if (dirty.length) {
    const ok = await dialogs.confirm({
      title: '修改內容並核發',
      message:
        `這次會以你修改後的內容簽發，調整的項目：${overrideLabels(dirty).join('、')}。\n` +
        '申請單本身不會被改寫，調整內容會記在稽核紀錄裡。要繼續嗎？',
      confirmText: '修改並核發',
    })
    if (!ok) return
  }
  busy.value = 'approve'
  try {
    const res = await ls.approveLicenseRequest(r.id, {
      review_note: reviewNote.value.trim() || null,
      ...pickOverride(draftBody(), dirty),
    })
    unknownModules.value = res.unknown_modules ?? []
    unknownQuotaKeys.value = res.unknown_quota_keys ?? []
    unknownModuleItems.value = res.unknown_module_items ?? []
    approving.value = null
    await reload()
    // 復站訊息併進同一則通知（兩個對話框接連跳出來只會讓人連按兩次「知道了」）。
    const resumed = res.resumed_site_id
      ? '\n\n這個站台先前因試用到期而停用，已同時派送復站任務（約 1 分鐘內恢復服務）。'
      : ''
    await announceIssued(
      res.issued_license_id,
      !!res.not_before_elapsed,
      // 批121：照原樣簽與改過再簽，在結果通知上要分得出來（`overridden` 是後端算的，
      // 不是前端的 `dirty`——以伺服器實際落地的為準）。
      res.overridden?.length
        ? `已依你調整後的內容簽發一張授權（調整了 ${overrideLabels(res.overridden).join('、')}）——`
        : '已依申請內容簽發一張授權——',
      resumed,
    )
  } catch (e) {
    await fail(e, '核發失敗')
  } finally {
    busy.value = null
  }
}

async function doReturn() {
  const r = approving.value
  const reason = returnReason.value.trim()
  if (!r || !reason) return
  busy.value = 'return'
  try {
    await ls.returnLicenseRequest(r.id, { reason })
    returnOpen.value = false
    approving.value = null
    await reload()
  } catch (e) {
    await fail(e, '退回失敗')
  } finally {
    busy.value = null
  }
}

async function doReject() {
  const r = approving.value
  if (!r) return
  busy.value = 'reject'
  try {
    await ls.rejectLicenseRequest(r.id, { reason: rejectReason.value.trim() || null })
    rejectOpen.value = false
    approving.value = null
    await reload()
  } catch (e) {
    await fail(e, '婉拒失敗')
  } finally {
    busy.value = null
  }
}

async function doComment() {
  const r = approving.value
  const text = commentText.value.trim()
  if (!r || !text) return
  busy.value = 'comment'
  try {
    await ls.commentLicenseRequest(r.id, text)
    commentOpen.value = false
    commentText.value = ''
    await reload()
    await dialogs.notify({
      title: '已附註記',
      message: '註記已送給送件方，草稿狀態不變——對方改完會再確認送出一次。',
    })
  } catch (e) {
    await fail(e, '附註記失敗')
  } finally {
    busy.value = null
  }
}

async function doCancel() {
  const r = approving.value
  if (!r) return
  const reason = cancelReason.value.trim()
  // 他人來源理由必填（後端也擋 422 `reason_required`，這裡先攔住免得白跑一趟）。
  if (cancelReasonRequired.value && !reason) return
  const ok = await dialogs.confirm({
    title: '撤回申請',
    message: '撤回這筆申請？內容會保留在「已取消」，但不會再被處理。',
    danger: true,
    confirmText: '撤回',
  })
  if (!ok) return
  busy.value = 'cancel'
  try {
    await ls.cancelLicenseRequest(r.id, reason ? { reason } : undefined)
    cancelOpen.value = false
    approving.value = null
    await reload()
  } catch (e) {
    await fail(e, '撤回失敗')
  } finally {
    busy.value = null
  }
}

/** 批81 需求 5：代管開站的核可框（`kind === 'hosted_provision'` 專用）。 */
const hostedApproving = ref<LicenseReq | null>(null)
async function afterHostedApprove() {
  hostedApproving.value = null
  await reload()
}

// ---------------------------------------------------------------------------
// 代客戶建立申請（貼上／上傳 request.json）——批93 起建立的是**草稿**
// ---------------------------------------------------------------------------
// 離線（氣隙）客戶把 `request.json` email 給我們時，後台原本沒有任何入口——只能請客戶
// 自己去 portal 上傳，或跳過申請流程硬簽一張沒有指紋、沒有稽核鏈的證。
const upOpen = ref(false)
const upCustomer = ref('')
const upProduct = ref('')
const upNote = ref('')
const upPlan = ref('')
const upText = ref('')
const upFileName = ref('')
const upBusy = ref(false)
const upFileInput = ref<HTMLInputElement | null>(null)

const upParsed = computed<Record<string, unknown> | null>(() => {
  const raw = upText.value.trim()
  if (!raw) return null
  try {
    const obj = JSON.parse(raw)
    return obj && typeof obj === 'object' && !Array.isArray(obj)
      ? (obj as Record<string, unknown>)
      : null
  } catch {
    return null
  }
})
const upBadJson = computed(() => !!upText.value.trim() && !upParsed.value)
const upInstanceId = computed(() => String(upParsed.value?.instance_id ?? '') || '—')
const upFingerprint = computed(() => {
  const fp = upParsed.value?.machine_fingerprint
  return fp && typeof fp === 'object' ? Object.keys(fp as object).join('、') : '—'
})
const upFileProduct = computed(() => String(upParsed.value?.product ?? ''))
// 批155：內部套裝只有營運主管以上列得出來。
const upPlans = computed(() => issuablePlans(store.productByKey(upProduct.value), { internal: auth.isManagerOrAbove }))
watch(upFileProduct, (v) => {
  if (v) upProduct.value = v
})

async function readUpFile(f: File) {
  upFileName.value = f.name
  upText.value = await f.text()
}
function onUpFile(e: Event) {
  const f = (e.target as HTMLInputElement).files?.[0]
  if (f) void readUpFile(f)
}
// 拖放邏輯抽在 `lib/fileDrop.ts`（攔 dragenter、抓不到檔案時明確提示）——理由見該檔開頭。
const { over: upDragOver, handlers: upDropHandlers } = useFileDrop({
  onFile: readUpFile,
  onNoFile: () => void dialogs.notify({ title: '沒有抓到檔案', message: NO_FILE_DROPPED_MESSAGE }),
})
// 卡片打開時整個視窗都接檔案拖放（不必精準拖進虛線框；放錯位置也不會變成瀏覽器直接開那個檔）。
useWindowFileDrop(() => upOpen.value, upDropHandlers)
function resetUpload() {
  upText.value = ''
  upFileName.value = ''
  upNote.value = ''
  upPlan.value = ''
  if (upFileInput.value) upFileInput.value.value = ''
}
/**
 * 產品必選（批114 B8 裁示 ㉚）：`settings.default_product_key` 已經移除，**沒帶產品的
 * 建草稿一律 422 `product_required`**。檔案自己寫了產品時仍可留「以檔案內容為準」
 * （後端會用檔案裡的值）；舊版 `install.sh` 產的檔案沒寫產品，就必須由操作者指定。
 */
const upNeedsProduct = computed(() => !upFileProduct.value && !upProduct.value)
const upReady = computed(
  () => !!upCustomer.value && !!upParsed.value && !upNeedsProduct.value && !upBusy.value,
)

async function submitUpload() {
  if (!upReady.value || !upParsed.value) return
  upBusy.value = true
  try {
    const res = await ls.createLicenseRequest({
      customer_id: upCustomer.value,
      request_json: upParsed.value,
      product: upProduct.value || null,
      note: upNote.value.trim() || null,
      plan: upPlan.value || null,
    })
    resetUpload()
    upOpen.value = false
    // 批186 順修：本來就在草稿籤才自己重載——換籤時 watch 已經會重載，以前這裡會再載一次。
    if (tab.value === 'draft') await reload()
    else tab.value = 'draft'
    await dialogs.notify({
      title: '已建立草稿',
      message: `已為「${store.customerName(upCustomer.value)}」建立一筆 ${store.productName(res.product)} 授權草稿。請在「草稿」頁籤按「檢視」把內容填好，再「確認送出」。`,
    })
  } catch (e) {
    await fail(e, '建立草稿失敗')
  } finally {
    upBusy.value = false
  }
}

// --- 顯示 helper -------------------------------------------------------------
const STATUS_LABELS: Record<string, string> = {
  draft: '草稿',
  pending: '待審核',
  approved: '已核發',
  rejected: '已婉拒',
  cancelled: '已取消',
}
function statusLabel(s: string): string {
  return STATUS_LABELS[s] ?? s
}
function statusClass(s: string): string {
  return (
    { draft: 'pending', pending: 'pending_activation', approved: 'active', rejected: 'revoked', cancelled: 'superseded' }[
      s
    ] ?? 'pending'
  )
}
const KIND_LABELS: Record<string, string> = {
  new: '新申請',
  change: '變更授權',
  renew: '續約',
  addon: '加購模組',
  hosted_provision: '代管開站',
}
function kindLabel(k: string | undefined): string {
  return KIND_LABELS[k ?? 'new'] ?? (k as string)
}
function loginLabel(r: LicenseReq): string | null {
  if (r.kind !== 'hosted_provision') return null
  const setup = (r.request_json?.setup ?? {}) as { login?: string }
  return setup.login === 'google' ? 'Google 登入' : '帳號密碼'
}
function fmtDate(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : '—'
}
function fmtDateTime(iso: string | null | undefined): string {
  return iso ? iso.replace('T', ' ').slice(0, 16) : '—'
}
function fpKeys(m: Record<string, string> | null | undefined): string {
  const keys = Object.keys(m ?? {})
  return keys.length ? keys.join('、') : '—'
}
/**
 * 模組摘要（批121 修）：**用憑證設計的中文名**，並比照配額做收合。
 *
 * ★ 原本是 `Object.keys(m).sort().join('、')`——直接印英文鍵（`ai、api、autopilot…`），
 *   而同一行的配額早就是中文（「座席 15・協作席次 不限・+11」）。同一句話裡兩種語言，
 *   而且全開的站台會把 11 個鍵整串攤開、把後面的到期日擠出視線（主人 2026-09-21 回報）。
 * ★ 名稱一律查 `spec.modules[].label`（與 `ModulePicker` 同一個來源）——LS 前端不准有
 *   產品字面，查不到就退回鍵名（寧可印代號也不要印空白）。
 * ★ 收合門檻與配額一致（`quotaSummaryText` 預設 2 項），超出的以 `+N` 帶過。
 */
const MODULE_SUMMARY_LIMIT = 3
function modulesText(m: Record<string, string> | null | undefined): string {
  const keys = Object.keys(m ?? {})
  if (!keys.length) return '（無）'
  const labels = new Map((approveSpec.value?.modules ?? []).map((x) => [x.key, x.label]))
  const names = keys.map((k) => labels.get(k) ?? k).sort((a, b) => a.localeCompare(b, 'zh-Hant'))
  const head = names.slice(0, MODULE_SUMMARY_LIMIT).join('、')
  const rest = names.length - MODULE_SUMMARY_LIMIT
  return rest > 0 ? `${head} +${rest}` : head
}
/**
 * 清單那一欄要顯示的配額（批114 裁示 ⑯）＝**申請內容**的配額。
 *
 * 批114 之前建的列只有相容數字欄（投影欄或 `request_json.requested` 的一級鍵），
 * 所以一律先過 `seedQuotas` 併進來——否則舊申請的那一欄會整片變成「—」。
 */
function rowQuotas(r: LicenseReq): Record<string, number | null> | null {
  // 只餵「申請內容」那一段（不給套裝與現行證）——這一欄要回答的是「客戶要求什麼」，
  // 不是「核發會簽出什麼」。
  return seedRequestDraft({
    kind: r.kind,
    spec: store.productByKey(r.product),
    request: r,
    raw: r.request_json?.requested,
  }).quotas
}

/**
 * 現行授權的配額摘要（批114 裁示 ⑰）——前兩個鍵＋`+N`，用憑證設計的中文名組字。
 * 舊證的相容數字欄先用 `seedQuotas` 併進來，否則批114 之前簽的證這一段會空掉。
 */
const currentQuotaText = computed(() => {
  const cur = approving.value?.current
  if (!cur) return ''
  return quotaSummaryText(approveSpec.value, seedQuotas(approveSpec.value, cur))
})

/** 對話框上半部的唯讀「申請脈絡」列（誰、什麼時候、什麼機器）。 */
const detailRows = computed<{ k: string; v: string }[]>(() => {
  const r = approving.value
  if (!r) return []
  const out: { k: string; v: string }[] = [
    { k: '客戶', v: store.customerName(r.customer_id) },
    { k: '來源', v: originLabel(r) },
    { k: '類型', v: kindLabel(r.kind) },
    { k: '產品', v: store.productName(r.product) },
    { k: '主機', v: r.instance_name || r.host_instance_id || '—' },
    { k: '指紋', v: fpKeys(r.machine_fingerprint) },
    { k: '申請人', v: r.requested_by ?? '—' },
    { k: '建立時間', v: fmtDateTime(r.created_at) },
  ]
  if (r.confirmed_at) {
    out.push({ k: '確認送出', v: `${fmtDateTime(r.confirmed_at)}（${r.confirmed_by ?? '—'}）` })
  }
  if (r.activation_policy) out.push({ k: '政策快照', v: activationPolicyLabel(r.activation_policy) })
  // 批155：平台送件的自動核准結果——核准了就講清楚沒有人工審核；沒核准就講為什麼轉人審。
  if (isAutoApproved(r)) {
    out.push({ k: '審核方式', v: '平台自動核准（平台與套裝的開關都開、內容逐字等於套裝模板，沒有經過人工審核）' })
  }
  const why = autoReviewReason(r)
  // 標籤欄寬 84px（約六個字）——「轉人工審核原因」七個字會把值擠歪。
  if (why) out.push({ k: '轉人審原因', v: reviewReasonLabel(why) })
  return out
})
/** 已結束的列：交代結果。 */
// --- 取證交付（批108）-------------------------------------------------------
//
// 核發完就能把證交給客戶。加購申請核發的是**子證**（`issued_license_id` 是 NULL），
// 所以要看 `issued_addon_id`——兩者互斥。
const takeoutBusy = ref(false)

const issuedArtifact = computed<{ kind: 'license' | 'addon'; id: string } | null>(() => {
  const r = approving.value
  if (!r || r.status !== 'approved') return null
  if (r.issued_license_id) return { kind: 'license', id: r.issued_license_id }
  if (r.issued_addon_id) return { kind: 'addon', id: r.issued_addon_id }
  return null
})

/** 取原文（下載與複製共用一支端點——兩種帶走方式都要留同一筆稽核）。 */
async function fetchIssuedBlob(): Promise<string | null> {
  const art = issuedArtifact.value
  if (!art) return null
  takeoutBusy.value = true
  try {
    const res =
      art.kind === 'license' ? await ls.licenseFile(art.id) : await ls.downloadAddonBlob(art.id)
    return res.blob
  } catch (e) {
    await fail(e, '取得憑證失敗')
    return null
  } finally {
    takeoutBusy.value = false
  }
}

async function downloadIssued() {
  const blob = await fetchIssuedBlob()
  if (!blob) return
  const isAddon = issuedArtifact.value?.kind === 'addon'
  saveTextFile(blob, isAddon ? ADDON_FILENAME : LICENSE_FILENAME)
  await dialogs.notify({
    title: '已下載憑證',
    message: `已存成 ${isAddon ? ADDON_FILENAME : LICENSE_FILENAME}。交給客戶放進站台的 license/ 目錄後重啟，或請他貼進站台〈系統 → 授權 → 手動安裝 .lic〉。`,
  })
}

async function copyIssued() {
  const blob = await fetchIssuedBlob()
  if (!blob) return
  if (await copyText(blob)) {
    await dialogs.notify({
      title: '已複製憑證',
      message: '貼進站台〈系統 → 授權 → 手動安裝 .lic〉即可。',
    })
  } else {
    await dialogs.notify({
      title: '無法自動複製',
      message: `這個瀏覽器不允許存取剪貼簿，請手動複製下面這段：\n\n${blob}`,
    })
  }
}

const outcomeRows = computed<{ k: string; v: string }[]>(() => {
  const r = approving.value
  if (!r || r.status === 'pending' || r.status === 'draft') return []
  const out = [{ k: '狀態', v: statusLabel(r.status) }]
  if (r.reviewed_at) {
    // 批155：自動核准沒有審核人（`reviewed_by` 留 null）——寫出來而不是一條槓。
    const who = r.reviewed_by ?? (isAutoApproved(r) ? '平台自動核准' : '—')
    out.push({ k: '處理時間', v: `${fmtDateTime(r.reviewed_at)}（${who}）` })
  }
  if (r.issued_license_id) out.push({ k: '核發的授權', v: r.issued_license_id })
  if (r.review_note) out.push({ k: '內部備註', v: r.review_note })
  const reason = reviewReason(r)
  if (reason) out.push({ k: '對外理由', v: reason })
  const cancel = (r.request_json?.cancel ?? null) as { at?: string; by?: string } | null
  if (cancel?.at) out.push({ k: '取消時間', v: `${fmtDateTime(cancel.at)}（${cancel.by ?? '—'}）` })
  return out
})
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>授權申請</h1>
      <span class="count">{{ store.requests.length }}</span>
      <span class="spacer" />
      <label class="hf">
        <span>來源</span>
        <select v-model="originFilter">
          <option value="">全部來源</option>
          <option value="admin">我方</option>
          <option value="reseller">代理商</option>
          <option value="portal">客戶自助</option>
          <option value="site">站台</option>
          <option value="platform">平台</option>
          <option :value="ORIGIN_AUTO_APPROVED">平台自動核准</option>
        </select>
      </label>
      <label v-if="platformOptions.length" class="hf">
        <span>平台</span>
        <select v-model="platformFilter">
          <option value="">全部</option>
          <option v-for="pl in platformOptions" :key="pl.id" :value="pl.id">{{ pl.name }}</option>
        </select>
      </label>
      <label v-if="resellerOptions.length" class="hf">
        <span>代理商</span>
        <select v-model="resellerFilter">
          <option value="">全部</option>
          <option v-for="r in resellerOptions" :key="r.id" :value="r.id">{{ r.name }}</option>
        </select>
      </label>
      <label class="hf">
        <span>產品</span>
        <select v-model="listProduct">
          <option value="">全部產品</option>
          <option v-for="p in store.products" :key="p.key" :value="p.key">{{ p.name }}</option>
        </select>
      </label>
      <!-- 首次載入完成前不給按：那時 watch 被 `ready` 擋著，清了也不會重載。 -->
      <FilterClearButton :dirty="filtersDirty && ready" @click="pf.clear()" />
      <UiButton variant="secondary" @click="upOpen = !upOpen">
        {{ upOpen ? '收起' : '＋ 貼上／上傳 request.json' }}
      </UiButton>
    </div>

    <!-- 代客戶建立草稿（離線客戶把 request.json email 給我們時的入口） -->
    <div v-if="upOpen" class="card" :class="{ 'drop-active': upDragOver }">
      <div class="card-head"><h3 class="section-title">代客戶建立授權草稿</h3></div>
      <p class="subtle">
        客戶在安裝主機跑 <code class="mono">install.sh</code> 後會產生
        <code class="mono">license/request.json</code>。把那個檔案拖進來（或直接貼上內容），
        會變成一筆<strong>草稿</strong>——內容填好、確認送出後才進審核流程，這樣指紋、產品與稽核鏈都留得下來。
      </p>
      <div class="up-grid">
        <label class="field">
          <span>客戶</span>
          <select v-model="upCustomer">
            <option value="">請選擇客戶…</option>
            <option v-for="c in store.customers" :key="c.id" :value="c.id">{{ customerOptionLabel(c) }}</option>
          </select>
        </label>
        <label class="field">
          <span>產品{{ upFileProduct ? '' : ' *' }}</span>
          <select v-model="upProduct">
            <option value="">{{ upFileProduct ? '（以檔案內容為準）' : '請選擇產品…' }}</option>
            <option v-for="p in store.products" :key="p.key" :value="p.key">{{ p.name }}</option>
          </select>
          <small v-if="upFileProduct" class="subtle">檔案寫的是 <code class="mono">{{ upFileProduct }}</code>；改成別的會被擋下（422）。</small>
          <!-- 批114（B8 裁示 ㉚）：系統不再有「預設產品」——舊版 install.sh 產的檔案
               沒寫產品時，要核發哪一個必須由操作者自己說。 -->
          <small v-else class="warn-text">檔案沒寫產品——請選擇要核發哪個產品。</small>
        </label>
        <label class="field">
          <span>套裝（選填）</span>
          <select v-model="upPlan">
            <option value="">（不指定）</option>
            <option v-for="p in upPlans" :key="p.key" :value="p.key">{{ p.label }}</option>
          </select>
        </label>
      </div>
      <div
        class="drop"
        :class="{ over: upDragOver }"
        v-on="upDropHandlers"
        @click="upFileInput?.click()"
      >
        <p>把 <strong>request.json</strong> 拖到這張卡片的任何位置，或<strong>點擊選擇檔案</strong></p>
        <input ref="upFileInput" type="file" accept=".json,application/json" hidden @change="onUpFile" />
      </div>
      <label class="field up-text">
        <span>或直接貼上內容</span>
        <textarea v-model="upText" rows="5" placeholder='{"product": "<產品代號>", "instance_id": "...", "machine_fingerprint": {...}}' />
      </label>
      <p v-if="upBadJson" class="warn-text">⚠ 這段內容不是合法的 JSON——請確認整個檔案都貼進來了。</p>
      <div v-if="upParsed" class="ro">
        <div class="ro-row"><span class="ro-k">檔案</span><span class="ro-v">{{ upFileName || '（貼上的內容）' }}</span></div>
        <div class="ro-row"><span class="ro-k">主機 ID</span><span class="ro-v"><code class="mono">{{ upInstanceId }}</code></span></div>
        <div class="ro-row"><span class="ro-k">指紋</span><span class="ro-v">{{ upFingerprint }}</span></div>
      </div>
      <label class="field up-text">
        <span>備註（選填）</span>
        <input v-model="upNote" maxlength="500" placeholder="例：客戶 9/8 來信附的檔案" />
      </label>
      <div class="up-acts">
        <UiButton :disabled="upBusy" @click="resetUpload">清除</UiButton>
        <UiButton variant="primary" :disabled="!upReady" :loading="upBusy" @click="submitUpload">
          建立草稿
        </UiButton>
      </div>
    </div>

    <div class="card">
      <div class="tabs filters" data-hscroll>
        <button
          v-for="t in TABS"
          :key="t.key"
          type="button"
          class="tab"
          :class="{ active: tab === t.key }"
          :title="t.hint"
          @click="tab = t.key"
        >
          {{ t.label }}<span class="tab-n">{{ counts[t.key] }}</span>
        </button>
        <!-- 「已退回」不是狀態（`return` 打回 draft），所以它是草稿頁籤裡的子篩選。 -->
        <label v-if="tab === 'draft' && returnedCount" class="sub-filter">
          <input v-model="onlyReturned" type="checkbox" />
          只看已退回（{{ returnedCount }}）
        </label>
        <!-- 批141：批次核准（只在〈待審核〉籤；代管開站申請不給勾）。 -->
        <template v-if="canBatch">
          <span class="spacer" />
          <span v-if="picked.length > BATCH_APPROVE_MAX" class="warn-text">一次最多 {{ BATCH_APPROVE_MAX }} 筆</span>
          <UiButton
            variant="primary"
            size="sm"
            :disabled="!picked.length || picked.length > BATCH_APPROVE_MAX"
            :loading="batchBusy"
            @click="runBatch"
          >
            批次核准（{{ picked.length }}）
          </UiButton>
        </template>
      </div>

      <div v-if="!visibleRequests.length" class="empty">
        <div class="empty-sub">這個分類目前沒有申請。</div>
      </div>
      <div v-else class="tbl-scroll" data-hscroll>
        <table v-card-table class="tbl card-table">
          <thead>
            <tr>
              <th v-if="canBatch" class="pick-h">
                <input
                  type="checkbox"
                  :checked="allPicked"
                  :disabled="!pickableRows.length"
                  title="全選這一頁可批次核准的申請"
                  @change="togglePickAll"
                />
              </th>
              <th>客戶</th><th>產品</th><th>來源</th><th>類型</th><th>主機</th>
              <th>申請人</th><th class="qcol">配額</th><th>狀態</th><th>時間</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in visibleRequests" :key="r.id">
              <td v-if="canBatch" class="pick">
                <input
                  type="checkbox"
                  :checked="picked.includes(r.id)"
                  :disabled="!batchEligible(r)"
                  :title="batchIneligibleReason(r) ?? '勾選以批次核准'"
                  @change="togglePick(r.id)"
                />
              </td>
              <td><span class="cust-cell">{{ store.customerName(r.customer_id) }}</span></td>
              <td class="prod">{{ store.productName(r.product) }}</td>
              <td class="prod">
                {{ originLabel(r) }}
                <!-- 批141：申請人是合作平台的組織，但這筆不是平台代送的（例如我方代建）——
                     來源欄說不出這件事，補一顆徽章。平台代送的列來源欄已經寫了平台名。 -->
                <div
                  v-if="r.platform && originOf(r) !== 'platform'"
                  class="chip plat"
                  :title="`合作平台「${r.platform.name}」的組織（${r.platform.external_ref}）`"
                >平台組織</div>
                <!-- 批155：平台送件、雙層開關都開且內容逐字等於模板 → 當場自動核准（沒有人工審核）。 -->
                <div
                  v-if="isAutoApproved(r)"
                  class="chip plat auto"
                  title="平台送件、內容逐字等於套裝模板，當場自動核准（沒有經過人工審核）"
                >自動核准</div>
              </td>
              <td>
                <span class="kind-label">{{ kindLabel(r.kind) }}</span>
                <div v-if="loginLabel(r)" class="subtle sub-line">{{ loginLabel(r) }}</div>
              </td>
              <td>
                <div class="hostcell">
                  <span>{{ r.instance_name || '—' }}</span>
                  <code v-if="r.host_instance_id" class="mono">{{ r.host_instance_id.slice(0, 12) }}</code>
                  <span v-if="r.hosted" class="status-pill hosted">代管</span>
                </div>
              </td>
              <td class="req-by">{{ r.requested_by ?? '—' }}</td>
              <!-- 批114 裁示 ⑯：原本那一欄只印某個產品的一個數字；改成**全部配額的
                   精簡摘要**（前兩項＋`+N`，游標移上去看全部）。舊列只有相容數字欄，
                   `rowQuotas` 會把它併進來。 -->
              <td class="qcol">
                <QuotaSummary :spec="store.productByKey(r.product)" :quotas="rowQuotas(r)" />
              </td>
              <td>
                <span class="status-pill" :class="statusClass(r.status)">{{ statusLabel(r.status) }}</span>
                <div v-if="reviewReason(r)" class="reason" :title="reviewReason(r) ?? ''">
                  {{ r.status === 'draft' ? '已退回：' : '' }}{{ reviewReason(r) }}
                </div>
              </td>
              <td class="nowrap tsmall">
                {{ fmtDateTime(r.reviewed_at ?? r.confirmed_at ?? r.created_at) }}
              </td>
              <td class="acts-cell">
                <UiButton variant="secondary" size="sm" @click="openDetail(r)">檢視</UiButton>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- keyset 分頁：後端一頁 50 筆。`next_cursor` 非 null 才畫這一列。
           ★ 批121：文案是「已顯示 N / 總數」——分母是**這個籤**的全表筆數，
           以前只印「已載入 N 筆」，看不出還剩多少，也看不出那個 N 是被籤篩過的。 -->
      <div v-if="store.requestsNextCursor" class="more">
        <UiButton size="sm" :loading="loadingMore" @click="loadMore">載入更多</UiButton>
        <span class="subtle">
          已顯示 {{ store.requests.length }} / {{ onlyReturned && tab === 'draft' ? returnedCount : counts[tab] }} 筆
        </span>
      </div>

      <p
        v-if="unknownModules.length || unknownQuotaKeys.length || unknownModuleItems.length"
        class="warn-text"
      >
        ⚠ 剛核發的授權內含這個產品憑證設計不認得的內容——
        <template v-if="unknownModules.length">模組代號：{{ unknownModules.join('、') }}；</template>
        <template v-if="unknownQuotaKeys.length">配額代號：{{ unknownQuotaKeys.join('、') }}；</template>
        <template v-if="unknownModuleItems.length">模組子項：{{ unknownModuleItems.join('、') }}；</template>
        產品端「代號缺席＝該模組停用」，請確認不是打錯字；如需修正請重新核發一張。
      </p>
    </div>

    <!-- 申請詳情：草稿可編輯（我方）／待審核唯讀＋核發動作／已結束交代結果 -->
    <UiModal
      :open="!!approving"
      width="760px"
      :title="`${statusLabel(approving?.status ?? '')} — ${approving ? store.customerName(approving.customer_id) : ''}`"
      @close="approving = null"
    >
      <template v-if="approving">
        <h4 class="sub-title">申請脈絡</h4>
        <div class="ro">
          <div v-for="row in detailRows" :key="row.k" class="ro-row">
            <span class="ro-k">{{ row.k }}</span><span class="ro-v">{{ row.v }}</span>
          </div>
        </div>
        <!-- 現行授權摘要（變更／續約最需要的對照基底） -->
        <div v-if="approving.current" class="ro cur">
          <div class="ro-row">
            <span class="ro-k">現行授權</span>
            <span class="ro-v">
              <!-- 批114 裁示 ⑰：配額不再逐欄硬編，改用憑證設計的標籤組字
                   （沒有宣告任何配額的產品會回空字串，整段自動省略）。 -->
              <code class="mono">{{ approving.current.license_id.slice(0, 8) }}</code>
              ・{{ planLabel(approveSpec, approving.current.plan) }}<template
                v-if="currentQuotaText"
              >・{{ currentQuotaText }}</template>・模組
              {{ modulesText(approving.current.modules) }}・到期
              {{ fmtDate(approving.current.expires_at) }}
            </span>
          </div>
        </div>
        <!-- 我方退回／註記的理由（送件方看得到的那一段） -->
        <p v-if="isDraft && reviewReason(approving)" class="reason-box">
          <strong>已退回：</strong>{{ reviewReason(approving) }}
        </p>

        <!-- 已結束＝唯讀交代結果 -->
        <template v-if="outcomeRows.length">
          <h4 class="sub-title">處理結果</h4>
          <div class="ro">
            <div v-for="row in outcomeRows" :key="row.k" class="ro-row">
              <span class="ro-k">{{ row.k }}</span><span class="ro-v">{{ row.v }}</span>
            </div>
          </div>

          <!-- 批108：核發完當場就能把證交出去。以前這裡只印一行 license_id，離線（氣隙）
               客戶的證要另外繞到〈授權〉頁去翻——而那頁在批108 之前根本沒有下載按鈕。 -->
          <div v-if="issuedArtifact && !approving?.hosted" class="act-row outcome-acts">
            <UiButton
              variant="primary"
              size="sm"
              :loading="takeoutBusy"
              @click="downloadIssued"
            >下載{{ issuedArtifact.kind === 'addon' ? '加購憑證' : '授權檔' }}</UiButton>
            <UiButton size="sm" :loading="takeoutBusy" @click="copyIssued">複製憑證</UiButton>
            <span class="subtle">
              離線（氣隙）客戶請用這裡取證，放進站台的 <code class="mono">license/</code> 目錄即可。
            </span>
          </div>
          <p v-else-if="issuedArtifact && approving?.hosted" class="subtle outcome-acts">
            這是代管站的授權，由中台自動派送到站台——不提供人工取證。
          </p>
        </template>

        <!-- 內容（草稿可編輯／pending 鎖定唯讀） -->
        <template v-if="isDraft || isPending">
          <h4 class="sub-title">
            授權內容
            <span v-if="!editable" class="lock">（唯讀）</span>
            <!-- 批121：pending 也能改了——講清楚改動的效力範圍，別讓人以為是在改申請單。 -->
            <span v-else-if="canOverride" class="lock edit">（可修改後核發）</span>
          </h4>
          <p v-if="canOverride" class="subtle">
            這裡的修改只影響<strong>這次核發的內容</strong>：申請單本身不會被改寫，
            調整了哪幾項會記在稽核紀錄裡。改完內容若偏離套裝模板，需要的核發身分可能會提高
            （下方會即時顯示）。
          </p>
          <!-- 批121：舊版站台送來的申請沒有完整配額包 → 未列出的配額會掉回套裝模板或缺席
               預設（協力夥伴席次缺席＝0）。這是主人回報「無上限變成 0」的來源，在這裡講一次。 -->
          <p v-if="missingQuotaPackage" class="warn-text">
            ⚠ 這台主機已有現行授權，但這筆申請<strong>沒有附完整的配額包</strong>（舊版站台送出的格式）：
            未列出的配額會依套裝模板、或該項自己的缺席預設簽發——<strong>缺席預設是 0 的項目就會變成 0</strong>，
            客戶原本的「無上限」不會被沿用。請確認下方「數量配額」是客戶該有的內容，必要時直接改完再核發。
          </p>
          <p v-if="isDraft && !editable" class="subtle">
            這是{{ originLabel(approving) }}的草稿——內容由送件方負責，我方不代改。
            要修正請用「附註記」告訴對方改哪裡；這筆不做了就用「撤回」並附理由。
            <!-- ★ 批97 修文案：草稿階段**沒有**「退回」（`return` 只受理 pending），
                 原文寫「退回附理由」會叫操作者去找一顆不存在的鈕。 -->
          </p>
          <!-- 批121：pending 的內容對**送件方**仍然鎖定；營運主管以上可以在核發當下調整
               （那一段說明由 `canOverride` 那個 <p> 負責，這裡只講「不能改的人看到什麼」）。 -->
          <p v-if="isPending && !canOverride" class="subtle">
            已確認送出的內容<strong>鎖定不可改</strong>——核發就是照這份原樣簽發。
            要調整內容請找營運主管以上的同事在核發時修改，或「退回草稿」（僅限我方／代理商來源）。
          </p>
          <p v-if="carriedOver.length" class="subtle carry">
            這是<strong>變更申請</strong>：申請單只列了要改的項目，
            <strong>{{ carriedOver.join('、') }}</strong>
            未列出＝<strong>沿用現行證</strong>（下方顯示的就是現行值）。
          </p>

          <div class="plan-row">
            <span class="plan-label">套裝</span>
            <button
              v-for="p in approvePlans"
              :key="p.key"
              type="button"
              class="plan-chip"
              :class="{ on: approvePlan === p.key }"
              :disabled="!editable || planLocked(p)"
              :title="planLocked(p) ? '內部套裝：只有營運主管以上能選' : undefined"
              @click="applyPlan(p.key)"
            >
              {{ p.label }}<span v-if="isInternalPlan(p)" class="int-mark">內部</span>
            </button>
            <button
              type="button"
              class="plan-chip"
              :class="{ on: approvePlan === '' }"
              :disabled="!editable"
              @click="approvePlan = ''"
            >
              客製
            </button>
          </div>
          <div class="form">
            <!-- 批114：兩個獨立的數量欄已拔除——數量一律在下方「數量配額」填
                 （同一個數字有兩個輸入框，而且獨立欄會靜默蓋掉配額表的值）。 -->
            <!-- 期限是兩個控制項（常用值下拉＋自由輸入），塞進 220px 的格子會擠爆
                 ——讓它獨佔一列（批114 版面修正）。 -->
            <label class="field wide">
              <span>期限</span>
              <!-- 批108：選項與自由輸入都是**總分鐘**（下拉常用值＋單位下拉自訂）。 -->
              <div class="term-row">
                <select v-model.number="form.term_total" :disabled="!editable">
                  <option v-for="m in approveTermOptions" :key="m" :value="m">{{ termLabel(m) }}</option>
                </select>
                <TermField v-if="editable" v-model="form.term_total" />
              </div>
            </label>
            <!-- 批113（#5521）：啟用／到期時刻。在這之前只有「換發」與「直簽」有這兩個欄位，
                 走草稿的申請路徑整條沒有——客戶頁填的值到這裡就消失了。 -->
            <label class="field">
              <span>啟用時間（選填）</span>
              <input v-model="form.not_before" type="datetime-local" :disabled="!editable" />
            </label>
            <label class="field">
              <span>到期方式</span>
              <select v-model="form.expiry_mode" :disabled="!editable">
                <option value="on_fetch">依期限（首次取證才起算）</option>
                <option value="at">指定到期時刻</option>
              </select>
            </label>
            <label v-if="form.expiry_mode === 'at'" class="field">
              <span>到期時刻</span>
              <input v-model="form.expires_at" type="datetime-local" :disabled="!editable" />
            </label>
          </div>
          <p v-if="form.expiry_mode === 'at'" class="subtle">
            到期時刻已指定——核發出來的證直接帶著它，不再依期限起算。
          </p>
          <p v-else-if="form.not_before" class="subtle">
            期限從<strong>啟用時間</strong>起算，到期日在核發當下就定好（不等首次取證）。
            啟用時間到核發時已經過去的話照簽，期限改從核發當下起算。
          </p>
          <div class="mp" :class="{ ro: !editable }">
            <ModulePicker
              :key="`${approving.id}-m-${pickerKey}`"
              v-model="approveModules"
              :spec="approveSpec"
              :allow-internal="auth.isManagerOrAbove"
            />
          </div>
          <p v-if="editable && !Object.keys(approveModules).length" class="warn-text">
            ⚠ 目前沒有勾選任何模組——這樣簽出去，站台的所有模組都會是停用狀態。
            請先點上方的套裝，或逐項勾選。
          </p>
          <h4 class="quota-title">數量配額</h4>
          <div class="mp" :class="{ ro: !editable }">
            <!-- 批114：必填與「可不可以無上限」由憑證設計宣告，編輯器自己判、以
                 `update:valid` 回報——送出鈕靠它反灰（取代批106 的單一數量欄特例）。 -->
            <!-- 批151：加購申請核發的是子證——加總的鍵是增加量（與代理商送件表單同一套提示）。 -->
            <QuotaEditor
              :key="`${approving.id}-q-${pickerKey}`"
              v-model="approveQuotas"
              :spec="approveSpec"
              :addon-context="approving.kind === 'addon'"
              @update:valid="quotasValid = $event"
            />
          </div>
          <!-- 批113（#5520）：行為參數（這個產品的憑證設計有宣告才出現，與客戶頁對稱）。 -->
          <template v-if="(approveSpec?.policies?.length ?? 0) > 0">
            <h4 class="quota-title">行為參數</h4>
            <div class="mp" :class="{ ro: !editable }">
              <PolicyEditor
                :key="`${approving.id}-p-${pickerKey}`"
                v-model="approvePolicyValues"
                :spec="approveSpec"
              />
            </div>
          </template>
          <label v-if="draftEditable" class="field note-field">
            <span>備註</span>
            <input v-model="form.note" maxlength="500" placeholder="內部備註，送件脈絡" />
          </label>
          <p v-else-if="approving.note" class="subtle">備註：{{ approving.note }}</p>

          <!-- 政策：按下去之前就要知道會發生什麼 -->
          <p class="policy" :class="{ ok: canDirect }">
            <strong>{{ activationPolicyLabel(policy.policy) }}</strong>
            <span v-if="policyText"> — {{ policyText }}</span>
          </p>
          <!-- ★ 批113（走查第 9 點）：加 `canDirect`——核發不了的人看到「核發後會留下
               自送自審標記」只會困惑（那顆按鈕對他是反灰的）。 -->
          <p v-if="isPending && isSelfMaker && canDirect" class="subtle self-note">
            這筆是<strong>你自己</strong>送出的——核發後稽核會留下「自送自審」的標記。
          </p>
        </template>

        <!-- 審核備註（只有真的能核發時才有意義） -->
        <label v-if="canApproveNow" class="field note-field">
          <span>審核備註（內部，客戶看不到）</span>
          <input v-model="reviewNote" maxlength="500" placeholder="例：9/9 已收款，依合約核發" />
        </label>

        <!-- 退回：pending → draft，附理由（送件方看得到） -->
        <div v-if="returnOpen" class="act-box warn-box">
          <label class="field">
            <span>退回理由（必填，送件方看得到）</span>
            <input
              v-model="returnReason"
              maxlength="500"
              placeholder="例：配額請照報價單調整，另外行事曆模組本期先不開。"
              v-enter="doReturn"
            />
          </label>
          <div class="act-row">
            <UiButton size="sm" @click="returnOpen = false">取消</UiButton>
            <UiButton
              variant="warn"
              size="sm"
              :disabled="!returnReason.trim()"
              :loading="busy === 'return'"
              @click="doReturn"
            >
              確認退回
            </UiButton>
          </div>
        </div>

        <!-- 附註記：對草稿留話，不改狀態 -->
        <div v-if="commentOpen" class="act-box">
          <label class="field">
            <span>給送件方的註記（不改狀態）</span>
            <input
              v-model="commentText"
              maxlength="500"
              placeholder="例：這台的指紋看起來是舊機器，請確認客戶已經換過主機了嗎？"
              v-enter="doComment"
            />
          </label>
          <div class="act-row">
            <UiButton size="sm" @click="commentOpen = false">取消</UiButton>
            <UiButton
              variant="secondary"
              size="sm"
              :disabled="!commentText.trim()"
              :loading="busy === 'comment'"
              @click="doComment"
            >
              送出註記
            </UiButton>
          </div>
        </div>

        <!-- 撤回（作廢）：draft／pending → cancelled。不分來源，他人來源理由必填 -->
        <div v-if="cancelOpen" class="act-box rej-box">
          <label class="field">
            <span>撤回理由（{{ cancelReasonRequired ? '必填，送件方看得到' : '選填' }}）</span>
            <input
              v-model="cancelReason"
              maxlength="500"
              placeholder="例：客戶改走直接採購，這張不用了。"
              v-enter="doCancel"
            />
          </label>
          <p class="subtle">
            撤回＝這筆不再處理，內容保留在「已取消」。客戶自助／代理商送的單子撤掉之後，對方會在自己的清單上看到這段理由。
          </p>
          <div class="act-row">
            <UiButton size="sm" @click="cancelOpen = false">取消</UiButton>
            <UiButton
              variant="danger"
              size="sm"
              :disabled="cancelReasonRequired && !cancelReason.trim()"
              :loading="busy === 'cancel'"
              @click="doCancel"
            >
              確認撤回
            </UiButton>
          </div>
        </div>

        <!-- 婉拒：pending → rejected -->
        <div v-if="rejectOpen" class="act-box rej-box">
          <label class="field">
            <span>婉拒理由（選填，客戶／代理商看得到）</span>
            <input
              v-model="rejectReason"
              maxlength="500"
              placeholder="例：貴司合約尚未到續約期，請於 10 月後再提出。"
              v-enter="doReject"
            />
          </label>
          <p class="subtle">
            婉拒＝這筆不做了。只是內容要改的話請用「退回草稿」，對方改完可以直接再送。
          </p>
          <div class="act-row">
            <UiButton size="sm" @click="rejectOpen = false">取消</UiButton>
            <UiButton variant="danger" size="sm" :loading="busy === 'reject'" @click="doReject">
              確認婉拒
            </UiButton>
          </div>
        </div>
      </template>

      <template #foot>
        <UiButton :disabled="!!busy" @click="approving = null">關閉</UiButton>
        <template v-if="approving && isDraft">
          <UiButton
            v-if="!draftEditable && !commentOpen"
            variant="secondary"
            :disabled="!!busy || cancelOpen"
            @click="commentOpen = true"
          >
            附註記
          </UiButton>
          <UiButton
            v-if="draftEditable"
            variant="secondary"
            :loading="busy === 'save'"
            :disabled="!!busy || cancelOpen"
            @click="saveOnly"
          >
            儲存草稿
          </UiButton>
          <!-- ★ 批97：**任何來源**的草稿都撤得動（作廢是我方的最終仲裁權），但撤別人的
               單子要附理由。改內容仍是所有權制——代理商的草稿要讓他自己改，
               我方的出口是「附註記」或「撤回附理由」。 -->
          <UiButton
            v-if="canCancel && !cancelOpen"
            variant="danger"
            :loading="busy === 'cancel'"
            :disabled="!!busy || commentOpen"
            @click="cancelOpen = true"
          >
            撤回
          </UiButton>
          <!-- 批114（原批106 的單一數量欄特例）：必填配額沒填，後端核發會回 422
               `quotas_invalid`。直發會當場炸；就算只是送進待審核，核發那一步也一定
               過不了、只能退回草稿——兩種情況都不如在這裡就擋住。 -->
          <UiButton
            v-if="draftEditable"
            variant="primary"
            :loading="busy === 'confirm'"
            :disabled="!!busy || cancelOpen || quotaMissing"
            :title="quotaMissing ? '下方「數量配額」還有必填項目沒填' : ''"
            @click="confirmDraft"
          >
            {{ confirmButtonLabel(canDirect) }}
          </UiButton>
        </template>
        <template v-else-if="approving && isPending">
          <!-- 批97：待審核也撤得動（他人來源附理由）。放在「婉拒」之前——
               作廢與婉拒是兩件事：婉拒是對客戶的答覆，撤回是把單子從流程裡拿掉。 -->
          <UiButton
            v-if="canCancel && !cancelOpen"
            variant="danger"
            :loading="busy === 'cancel'"
            :disabled="!!busy || returnOpen || rejectOpen"
            @click="cancelOpen = true"
          >
            撤回
          </UiButton>
          <UiButton
            v-if="!rejectOpen"
            variant="danger"
            :disabled="!!busy || returnOpen || cancelOpen"
            @click="rejectOpen = true"
          >
            婉拒
          </UiButton>
          <!-- 「退回草稿」只對有草稿階段的來源有意義（後端擋 409 `no_draft_stage`）：
               客戶自助／站台來源沒有改草稿的介面，退回去就是死單。 -->
          <UiButton
            v-if="
              !returnOpen && approving && !NO_DRAFT_ORIGINS.includes(originOf(approving))
            "
            variant="warn"
            :disabled="!!busy || rejectOpen || cancelOpen"
            @click="returnOpen = true"
          >
            退回草稿
          </UiButton>
          <UiButton
            variant="primary"
            :disabled="!canApproveNow || !!busy || returnOpen || rejectOpen || cancelOpen"
            :loading="busy === 'approve'"
            :title="!canDirect ? '這份內容的核發政策需要更高身分' : undefined"
            @click="approve"
          >
            {{ dirtyKeys.length ? '修改並核發' : '核發' }}
          </UiButton>
        </template>
      </template>
    </UiModal>

    <!-- 批81 需求 5：核可代管開站（選主機＋容量檢查＋完整授權表單） -->
    <HostedApproveModal
      :request="hostedApproving"
      :customer-name="hostedApproving ? store.customerName(hostedApproving.customer_id) : ''"
      @close="hostedApproving = null"
      @done="afterHostedApprove"
    />
    <!-- 批141：批次核准的結果（逐筆：成功帶授權編號、失敗帶原因） -->
    <UiModal :open="batchOpen" title="批次核准結果" width="720px" @close="batchOpen = false">
      <p class="batch-sum">
        送出 {{ batchRows.length }} 筆：<strong class="batch-ok">核發 {{ batchOkCount }} 筆</strong>、
        <strong :class="batchFailCount ? 'batch-bad' : ''">未核准 {{ batchFailCount }} 筆</strong>。
        <!-- 批141-R：全部核發時不再提「未核准的那幾筆」（0 筆時這句話講的是不存在的東西）。 -->
        <template v-if="batchFailCount">未核准的那幾筆仍在〈待審核〉，可以逐筆打開處理。</template>
      </p>
      <div class="tbl-scroll" data-hscroll>
        <table v-card-table class="tbl card-table">
          <thead>
            <tr><th>客戶</th><th>結果</th><th>授權編號／原因</th></tr>
          </thead>
          <tbody>
            <tr v-for="row in batchRows" :key="row.id">
              <td>{{ row.customer }}</td>
              <td class="nowrap">
                <span class="status-pill" :class="row.ok ? 'active' : 'revoked'">{{ row.ok ? '已核發' : '未核准' }}</span>
              </td>
              <td>
                <code v-if="row.ok" class="mono">{{ row.text }}</code>
                <span v-else>{{ row.text }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <template #foot>
        <UiButton variant="primary" @click="batchOpen = false">關閉</UiButton>
      </template>
    </UiModal>
  </div>
</template>

<style scoped>
/* 批141：批次核准的勾選欄與平台徽章 */
.tbl th.pick-h,
.tbl td.pick {
  width: 20px;
  padding-left: 10px;
  padding-right: 2px;
}
/* 批141：多了勾選欄之後表格變擠——客戶名與類型不要被擠成一個字一行（inline-block 的
   min-width 才撐得住欄寬，td 自己的 min-width 瀏覽器不理），寬度改由申請人 email 讓出。 */
.cust-cell {
  display: inline-block;
  min-width: 6em;
}
.kind-label {
  white-space: nowrap;
}
.req-by {
  word-break: break-all;
}
.chip.plat {
  margin-top: 2px;
  background: var(--st-transferred-bg);
  color: var(--st-transferred-fg);
  border-color: transparent;
}
/* 批155：平台自動核准（沒有人工審核）——與「平台組織」同一個位置，用「已核發」的綠底區分。 */
.chip.plat.auto {
  background: var(--st-active-bg);
  color: var(--st-active-fg);
}
/* 批155：內部套裝 chip 後面的小字標記。 */
.int-mark {
  margin-left: 4px;
  font-size: var(--fs-11);
  opacity: 0.8;
}
.batch-ok {
  color: var(--brand-600);
}
.batch-bad {
  color: var(--danger);
}
.batch-sum {
  margin: 0 0 var(--sp-3);
  font-size: var(--fs-13);
  color: var(--fg-2);
}
/* keyset 分頁的「載入更多」列（形狀與 LicensesView 一致） */
.more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  padding: var(--sp-3);
  border-top: 1px solid var(--border);
}
.page-head {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-bottom: var(--sp-4);
}
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}
.spacer {
  flex: 1;
}
.hf {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--fs-12);
  color: var(--fg-3);
}
.filters {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-bottom: var(--sp-3);
}
.tab-n {
  margin-left: 6px;
  color: var(--fg-4);
  font-size: var(--fs-11);
}
.sub-filter {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: var(--sp-2);
  font-size: var(--fs-12);
  color: var(--fg-3);
  white-space: nowrap;
}
/* 代客戶建立草稿卡（版型抄客戶 portal 的上傳卡） */
.up-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--sp-3);
  margin-bottom: var(--sp-3);
}
.drop {
  border: 1px dashed var(--border-strong);
  border-radius: var(--r-md);
  padding: var(--sp-5);
  text-align: center;
  color: var(--fg-3);
  font-size: var(--fs-13);
  cursor: pointer;
  transition: all var(--dur-fast) var(--ease);
}
.drop:hover,
.drop.over {
  border-color: var(--brand-400);
  background: var(--brand-50);
}
/* 檔案一拖進視窗，整張卡片亮起來（1.0.47：接收面＝整個視窗，放哪裡都行，這裡告訴使用者「放開就對了」）。 */
.card.drop-active {
  outline: 2px dashed var(--brand-400);
  outline-offset: 2px;
  background: var(--brand-50);
}
.drop p {
  margin: 0;
}
.up-text {
  margin-top: var(--sp-3);
}
.up-text textarea {
  font-family: var(--font-mono, monospace);
  font-size: var(--fs-12);
  padding: 8px 10px;
  border: 1px solid var(--border-strong);
  border-radius: var(--r-sm);
  background: var(--bg-panel);
  color: var(--fg);
  resize: vertical;
}
.up-acts {
  display: flex;
  justify-content: flex-end;
  gap: var(--sp-2);
  margin-top: var(--sp-3);
}
.prod {
  white-space: nowrap;
}
/* 配額摘要欄（批114）：窄欄一行不折行，過長由 `QuotaSummary` 自己以省略號收尾。 */
.qcol {
  max-width: 190px;
}
.nowrap {
  white-space: nowrap;
}
.tsmall {
  font-size: var(--fs-12);
  color: var(--fg-3);
}
.hostcell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
}
.status-pill.muted {
  background: var(--bg-hover);
  color: var(--fg-3);
}
.sub-line {
  margin-top: 3px;
  font-size: var(--fs-11);
  line-height: var(--lh-base);
}
.reason {
  margin-top: 3px;
  max-width: 240px;
  font-size: var(--fs-11);
  color: var(--fg-3);
  line-height: var(--lh-base);
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
/* 對話框：唯讀資訊區（與 HostedApproveModal 同一套視覺） */
.sub-title {
  margin: var(--sp-4) 0 var(--sp-2);
  font-size: var(--fs-13);
  color: var(--fg-3);
  font-weight: 600;
}
.sub-title:first-child {
  margin-top: 0;
}
/* 變更申請的「沿用現行證」提示：與唯讀說明同一層級，但要看得出是另一件事。 */
.carry {
  border-left: 3px solid var(--border);
  padding-left: 8px;
}
.lock {
  font-weight: 400;
  color: var(--fg-4);
}
.ro {
  display: grid;
  gap: 4px;
  padding: var(--sp-3);
  border: 1px solid var(--border);
  border-radius: var(--r-md);
  background: var(--bg);
}
.ro.cur {
  margin-top: var(--sp-2);
}
.ro-row {
  display: flex;
  gap: var(--sp-3);
  font-size: var(--fs-13);
}
.ro-k {
  color: var(--fg-3);
  min-width: 84px;
  flex: none;
}
.ro-v {
  color: var(--fg);
  word-break: break-word;
}
.reason-box {
  margin: var(--sp-3) 0 0;
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--warning);
  border-radius: var(--r-sm);
  color: var(--fg-2);
  font-size: var(--fs-12);
  line-height: var(--lh-base);
}
.plan-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--sp-2);
  margin: var(--sp-3) 0;
}
.plan-label {
  font-size: var(--fs-13);
  color: var(--fg-3);
}
.plan-chip {
  padding: 4px 12px;
  border: 1px solid var(--border-2);
  border-radius: 999px;
  background: transparent;
  color: var(--fg-2);
  font-size: var(--fs-13);
  cursor: pointer;
}
.plan-chip:disabled {
  cursor: default;
  opacity: 0.75;
}
.plan-chip.on {
  border-color: var(--brand-500);
  background: var(--brand-500);
  color: #fff;
}
/* 草稿內容的欄位區（批114 改版面）。
   原本是 `flex-wrap` ＋ `min-width:120px`：期限的下拉、自由輸入、啟用時間、到期方式
   全部擠在同一列，每欄窄到只剩 120px，`datetime-local` 的日期還會被切掉半截
   （主人 09-17：「這些輸入全擠在一起」）。改成 auto-fit 網格，一欄至少 220px，
   放得下就並排、放不下就自動換行，每一欄的寬度也一致。 */
.form {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--sp-3);
  margin: 0 0 var(--sp-3);
}
.form .field {
  display: grid;
  gap: 4px;
  align-content: start;
}
/* 獨佔一整列的欄位（期限）。 */
.form .field.wide {
  grid-column: 1 / -1;
}
/* 期限＝「常用值下拉」＋「數字＋單位」兩件事，橫向排在同一列。 */
.term-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--sp-2);
}
.term-row select {
  flex: 1 1 160px;
  min-width: 0;
}
/* 上傳卡欄位裡的提示字：`.warn-text` 的上邊距是為段落設計的，貼在欄位下面太鬆。 */
.field .warn-text {
  margin-top: 4px;
}
.field span {
  font-size: var(--fs-12);
  color: var(--fg-2);
}
.note-field {
  display: grid;
  gap: 4px;
  margin-top: var(--sp-3);
}
.quota-title {
  margin: 0 0 var(--sp-1);
  font-size: var(--fs-13);
  color: var(--fg-3);
  font-weight: 600;
}
.mp {
  margin-bottom: var(--sp-3);
}
/* 唯讀時把編輯器整塊變淡並吃掉點擊——內容鎖定就是鎖定，不要讓人以為改得動。
   （真正的防線是後端；這裡只是不要誤導。） */
.mp.ro {
  opacity: 0.85;
  pointer-events: none;
}
/* 政策提示 */
.policy {
  margin: var(--sp-3) 0 0;
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--border-strong);
  border-radius: var(--r-sm);
  background: var(--bg);
  color: var(--fg-2);
  font-size: var(--fs-12);
  line-height: var(--lh-base);
}
.policy.ok {
  border-color: var(--brand-400);
  background: var(--brand-50);
  color: var(--brand-700, var(--fg));
}
/* 同框展開的動作區（退回／註記／拒絕；不再開第二層 modal） */
.act-box {
  margin-top: var(--sp-3);
  padding: var(--sp-3);
  border: 1px solid var(--border-strong);
  border-radius: var(--r-md);
  background: var(--bg);
}
.act-box.warn-box {
  border-color: var(--warning);
}
.act-box.rej-box {
  border-color: var(--danger);
}
.act-box .field {
  display: grid;
  gap: 4px;
  margin-bottom: var(--sp-2);
}
.act-row {
  display: flex;
  justify-content: flex-end;
  gap: var(--sp-2);
}
/* ★ 不可叫 `.warn`：scoped 樣式會套到子元件根節點，UiButton 的 `variant="warn"` 根就是
   `<button class="btn warn">`，會吃到這裡的 margin-top 把 footer 的按鈕推低一截。 */
.warn-text {
  margin: var(--sp-3) 0 0;
  color: var(--warning);
  font-size: var(--fs-12);
  line-height: var(--lh-base);
}
/* 手機／平板（批171）：表格放不下時申請人 email 與主機 iid 被壓成一行三四個字（`break-all`）
   → 兩者不折行，寬度不夠就整張表橫捲（操作欄釘在右側）。手機上是卡片，不受影響。 */
@media (--compact) {
  .req-by,
  .hostcell code {
    word-break: normal;
    white-space: nowrap;
  }
}
</style>
